import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
export type AdvancedChartTooltipData = {
  date: string;
  // Y-axis values (for dynamic tooltip on axes)
  yAxisPrice: number;
  yAxisFairPrice: number;
  yAxisRevenue: number;
  yAxisPE: number;
  yAxisEps: number;
  yAxisCash: number;
  yAxisTotalAssets: number;
  yAxisShortTermDebt: number;
  yAxisLongTermDebt: number;
  yAxisTotalLiabilities: number;
  yAxisShareholdersEquity: number;
  yAxisOperatingCashFlow: number;
  yAxisFreeCashFlow: number;
  yAxisDividends: number;
  yAxisBuybacks: number;
  yAxisGrossProfitMargin: number;
  yAxisOperatingMargin: number;
  yAxisNetMargin: number;
  yAxisOperatingCFMargin: number;
  yAxisFreeCFMargin: number;
  yAxisOCFToNetIncome: number;
  yAxisFCFToNetIncome: number;
  yAxisROIC: number;
  yAxisFlatEPSTTM: number;
  yAxisFlatOCFTTM: number;
  yAxisFairPriceOCF: number;
  // Actual graph data values (for static tooltip-no-data)
  graphPrice: number;
  graphRevenue: number;
  graphNetIncome: number;
  graphGrossProfit: number;
  graphOperatingIncome: number;
  graphPretaxIncome: number;
  graphPE: number;
  graphEps: number;
  graphCash: number;
  graphTotalAssets: number;
  graphShortTermDebt: number;
  graphLongTermDebt: number;
  graphTotalLiabilities: number;
  graphShareholdersEquity: number;
  graphOperatingCashFlow: number;
  graphFreeCashFlow: number;
  graphDividends: number;
  graphBuybacks: number;
  graphGrossProfitMargin: number;
  graphOperatingMargin: number;
  graphNetMargin: number;
  graphOperatingCFMargin: number;
  graphFreeCFMargin: number;
  graphOCFToNetIncome: number;
  graphFCFToNetIncome: number;
  graphROIC: number;
  graphFlatEPSTTM: number;
  graphFairPrice: number;
  graphFairPriceFlatEPSTTM: number;
  graphEntryPrice: number;
  graphExitPrice: number;
  hoveredElementId?: string | null;
  hoveredElementLabel?: string | null;
  hoveredElementValue?: number | null;
  chartArea: any;
  mouseX: number;
  mouseY: number;
  pricePixelY?: number | null;
  revenuePixelY?: number | null;
  netIncomePixelY?: number | null;
  grossProfitPixelY?: number | null;
  operatingIncomePixelY?: number | null;
  pretaxIncomePixelY?: number | null;
  pePixelY?: number | null;
  epsPixelY?: number | null;
  cashPixelY?: number | null;
  totalAssetsPixelY?: number | null;
  shortTermDebtPixelY?: number | null;
  longTermDebtPixelY?: number | null;
  totalLiabilitiesPixelY?: number | null;
  shareholdersEquityPixelY?: number | null;
  operatingCashFlowPixelY?: number | null;
  freeCashFlowPixelY?: number | null;
  dividendsPixelY?: number | null;
  buybacksPixelY?: number | null;
  grossProfitMarginPixelY?: number | null;
  operatingMarginPixelY?: number | null;
  netMarginPixelY?: number | null;
  operatingCFMarginPixelY?: number | null;
  freeCFMarginPixelY?: number | null;
  ocfToNetIncomePixelY?: number | null;
  fcfToNetIncomePixelY?: number | null;
  roicPixelY?: number | null;
  flatEPSTTMPixelY?: number | null;
  fairPricePixelY?: number | null;
  fairPriceFlatEPSTTMPixelY?: number | null;
  entryPricePixelY?: number | null;
  exitPricePixelY?: number | null;
  // Optional series used by screens that enrich tooltip content.
  yAxisPfcf?: number;
  yAxisPocf?: number;
  graphPfcf?: number;
  graphPocf?: number;
  graphFlatOCFTTM?: number;
  graphFairPriceOCF?: number;
  graphEntryPriceOCF?: number;
  graphExitPriceOCF?: number;
  pfcfPixelY?: number | null;
  pocfPixelY?: number | null;
  flatOCFTTMPixelY?: number | null;
  fairPriceOcfPixelY?: number | null;
  entryPriceOcfPixelY?: number | null;
  exitPriceOcfPixelY?: number | null;
};

// Helper function to format financial values coming in as "millions USD".
// Display as K (thousands of millions) with 2 decimals.
function formatRevenueToK(value: number): string {
  if (!Number.isFinite(value)) return '0.00K';
  const valueInK = value / 1000;
  return `${valueInK.toFixed(2)}K`;
}

// Revenue-only formatter: show K/M/B based on magnitude (values are in millions).
export function formatRevenueWithUnits(value: number): string {
  if (!Number.isFinite(value)) return '0.00M';
  const absValue = Math.abs(value);
  const roundUp = (num: number, decimals: number) => {
    const factor = 10 ** decimals;
    return Math.ceil(num * factor) / factor;
  };

  if (absValue >= 1000) {
    const valueInB = roundUp(value / 1000, 2);
    return `${valueInB.toFixed(2)}B`;
  }

  if (absValue >= 1) {
    const valueInM = roundUp(value, 2);
    return `${valueInM.toFixed(2)}M`;
  }

  const valueInK = roundUp(value * 1000, 2);
  return `${valueInK.toFixed(2)}K`;
}

function formatAxisTickNumber(value: number): string {
  if (!Number.isFinite(value)) return '';

  const roundedInt = Math.round(value);
  if (Math.abs(value - roundedInt) < 1e-6) return String(roundedInt);

  // Keep enough precision so adjacent ticks don't collapse to the same label.
  // Trim trailing zeros for a clean display.
  return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
}

function formatXAxisTooltipDate(dateIso: string): string {
  const d = new Date(dateIso);
  const t = d.getTime();
  if (!Number.isFinite(t)) return dateIso;

  // Display as DD-MM-YYYY (day-month-year) to match the requested UI.
  // Use UTC parts to avoid timezone day-shift on boundary dates.
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy}`;
}

const ISO_DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseTimeBound(value: string | Date | number, endOfDay: boolean): number {
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' && ISO_DATE_KEY_RE.test(value)) {
    // Interpret a date-key as an explicit UTC bound to avoid timezone shifts.
    return new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`).getTime();
  }

  return new Date(value as any).getTime();
}

const DEFAULT_TOOLTIP_FONT_SIZE = '0.6875rem'; // 11px
const RIGHT_AXIS_TOOLTIP_FONT_SIZE = DEFAULT_TOOLTIP_FONT_SIZE;
const RIGHT_AXIS_TOOLTIP_WIDTH_PX = 60;
const RIGHT_AXIS_PE_TOOLTIP_WIDTH_PX = 42;

// Crosshair plugin with mouse tracking
let mouseX: number | null = null;
let mouseY: number | null = null;
let lastRevenueMouseX: number | null = null;
let lastStepMouseX: number | null = null;
type HoveredSegmentInfo = {
  elementId: string;
  label: string;
  value: number;
  pixelX0: number;
  pixelX1: number;
  pixelY: number;
  color: string;
  baseLineWidth: number;
  distancePx: number;
} | null;

const HOVER_SEGMENT_TOLERANCE_PX = 4;
let hoveredSegmentInfo: HoveredSegmentInfo = null;

let tooltipCallback: ((data: AdvancedChartTooltipData | null) => void) | null = null;
let axisPositionCallback: ((positions: { y: number; y1: number; y2: number; y3: number; y4: number; y5: number; y6: number; y7: number; y8: number; y9: number; y10: number; y11: number; y12: number; y13: number; y14: number; y15: number; y16: number; y17: number; y18: number; y19: number; y20: number; y21: number; y22: number; y23: number }) => void) | null = null;
let globalChartInstance: any = null; // Store chart instance globally
let activeCrosshairChart: any = null;
let globalLeaveListenerAttached = false;

const clearCrosshairState = () => {
  mouseX = null;
  mouseY = null;
  lastRevenueMouseX = null;
  lastStepMouseX = null;
  hoveredSegmentInfo = null;
  if (tooltipCallback) {
    tooltipCallback(null);
  }
};

const ensureGlobalLeaveListener = () => {
  if (globalLeaveListenerAttached) return;
  if (typeof document === 'undefined') return;
  globalLeaveListenerAttached = true;

  const handler = (ev: Event) => {
    const chart = activeCrosshairChart;
    if (!chart) return;

    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    if (!canvas) {
      activeCrosshairChart = null;
      return;
    }

    const target = (ev as any)?.target as Node | null;
    // As soon as the pointer is no longer over this canvas, clear + redraw it.
    if (target && (target === canvas || canvas.contains(target))) {
      return;
    }

    clearCrosshairState();
    try {
      chart.draw();
    } catch {
      // ignore redraw failures
    }
    activeCrosshairChart = null;
  };
 
  // Capture so we still see events even if something overlays the canvas.
  document.addEventListener('pointermove', handler, { passive: true, capture: true } as any);
  document.addEventListener('mousemove', handler, { passive: true, capture: true } as any);
};

const crosshairPlugin: Plugin<'line'> = {
  id: 'crosshair',
  // Opt-in: only charts that set `options.plugins.crosshair.enabled = true`
  // will run the crosshair logic. This prevents global crosshair state from
  // affecting other charts (e.g. Research → Key Metrics grid).
  //
  // (Chart.js plugin options are untyped here)
  //
  // We intentionally keep the checks lightweight.
  afterInit(chart) {
    const enabled = Boolean((chart.options as any)?.plugins?.crosshair?.enabled);
    if (!enabled) return;

    ensureGlobalLeaveListener();

    // Use native canvas leave events too (not only Chart.js events).
    // When moving between separate chart canvases, Chart.js doesn't always
    // deliver a reliable 'mouseout' to the old chart, so the last dashed
    // crosshair can remain rendered.
    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    if (!canvas || typeof canvas.addEventListener !== 'function') return;

    const handler = () => {
      clearCrosshairState();
      try {
        chart.draw();
      } catch {
        // ignore redraw failures
      }
      if (activeCrosshairChart === chart) {
        activeCrosshairChart = null;
      }
    };
    canvas.addEventListener('mouseout', handler);
  },
  beforeDestroy(chart) {
    const enabled = Boolean((chart.options as any)?.plugins?.crosshair?.enabled);
    if (!enabled) return;

    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    const handler = (chart as any).$crosshairLeaveHandler as (() => void) | undefined;
    if (canvas && handler) {
      canvas.removeEventListener('mouseleave', handler);
      canvas.removeEventListener('pointerleave', handler);
      canvas.removeEventListener('mouseout', handler);
    }
    delete (chart as any).$crosshairLeaveHandler;
  },
  afterEvent(chart, args) {
    const { event } = args;
    const { chartArea } = chart;

    // If the cursor moved onto a different chart canvas, the previous chart
    // may not always receive a reliable 'mouseout'. Force a redraw of the
    // previous chart with cleared hover state so dashed crosshair lines don't
    // get stuck on the old chart.
    const prevChart = globalChartInstance;
    const isMouseMove = event.type === 'mousemove';
    if (isMouseMove && prevChart && prevChart !== chart) {
      clearCrosshairState();
      try {
        prevChart.draw();
      } catch {
        // ignore redraw failures
      }
    }

    // Store chart instance for later use
    if (globalChartInstance !== chart) {
      globalChartInstance = chart;
    }
    
    if (event.type === 'mousemove') {
      const canvasPosition = event as any;
      if (canvasPosition.x !== undefined && canvasPosition.y !== undefined) {
        const tempX = canvasPosition.x;
        const tempY = canvasPosition.y;
        
        // Keep tooltips responsive even when hovering over axes/margins.
        // Require Y to be within the plot's vertical span, but allow X anywhere.
        // X is clamped to the plot area later for snapping and crosshair drawing.
        if (tempY >= chartArea.top && tempY <= chartArea.bottom) {
          // Switching between separate chart canvases doesn't always deliver a
          // reliable leave event to the previous chart. As soon as we detect
          // we're hovering a different chart, clear + redraw the previous one
          // to erase any dashed crosshair that was drawn there.
          if (activeCrosshairChart && activeCrosshairChart !== chart) {
            clearCrosshairState();
            try {
              activeCrosshairChart.draw();
            } catch {
              // ignore redraw failures
            }
          }

          mouseX = tempX;
          mouseY = tempY;
          activeCrosshairChart = chart;
          chart.draw();
        } else {
          clearCrosshairState();
          if (activeCrosshairChart === chart) {
            activeCrosshairChart = null;
          }
          chart.draw();
        }
      }
    } else {
      const nativeType = (event as any)?.native?.type as string | undefined;
      const isLeaveEvent = event.type === 'mouseout' || nativeType === 'mouseleave';
      if (!isLeaveEvent) return;
      clearCrosshairState();
      chart.draw();
      if (activeCrosshairChart === chart) {
        activeCrosshairChart = null;
      }
    }
  },
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    
    // Check if required scales exist
    if (!chartArea || !scales) {
      return;
    }
    
    const { left, right, top, bottom } = chartArea;
    const { x, y, y1, y2, y3, y4, y5, y6, y7, y8, y9, y10, y11, y12, y13, y14, y15, y16, y17, y18, y19, y20, y21, y22, y23 } = scales;
    
    // Exit early if essential scales are missing
    if (!x || !y) {
      return;
    }
    
    // Calculate axis center positions for tooltips
    // Each axis has left and right properties showing its actual position
    // Only calculate center for axes that are actually displayed (width > 0)
    const yAxisCenter = y && (y as any).width > 0 ? ((y as any).left + (y as any).right) / 2 : 0;
    const y1AxisCenter = y1 && (y1 as any).width > 0 ? ((y1 as any).left + (y1 as any).right) / 2 : 0;
    const y2AxisCenter = y2 && (y2 as any).width > 0 ? ((y2 as any).left + (y2 as any).right) / 2 : 0;
    const y3AxisCenter = y3 && (y3 as any).width > 0 ? ((y3 as any).left + (y3 as any).right) / 2 : 0;
    const y4AxisCenter = y4 && (y4 as any).width > 0 ? ((y4 as any).left + (y4 as any).right) / 2 : 0;
    const y5AxisCenter = y5 && (y5 as any).width > 0 ? ((y5 as any).left + (y5 as any).right) / 2 : 0;
    const y6AxisCenter = y6 && (y6 as any).width > 0 ? ((y6 as any).left + (y6 as any).right) / 2 : 0;
    const y7AxisCenter = y7 && (y7 as any).width > 0 ? ((y7 as any).left + (y7 as any).right) / 2 : 0;
    const y8AxisCenter = y8 && (y8 as any).width > 0 ? ((y8 as any).left + (y8 as any).right) / 2 : 0;
    const y9AxisCenter = y9 && (y9 as any).width > 0 ? ((y9 as any).left + (y9 as any).right) / 2 : 0;
    const y10AxisCenter = y10 && (y10 as any).width > 0 ? ((y10 as any).left + (y10 as any).right) / 2 : 0;
    // Free Cash Flow shares y10; keep y11 as an alias for legacy positioning logic.
    const y11AxisCenter = y11 && (y11 as any).width > 0 ? ((y11 as any).left + (y11 as any).right) / 2 : y10AxisCenter;
    const y12AxisCenter = y12 && (y12 as any).width > 0 ? ((y12 as any).left + (y12 as any).right) / 2 : 0;
    const y13AxisCenter = y13 && (y13 as any).width > 0 ? ((y13 as any).left + (y13 as any).right) / 2 : 0;
    const y14AxisCenter = y14 && (y14 as any).width > 0 ? ((y14 as any).left + (y14 as any).right) / 2 : 0;
    const y15AxisCenter = y15 && (y15 as any).width > 0 ? ((y15 as any).left + (y15 as any).right) / 2 : 0;
    const y16AxisCenter = y16 && (y16 as any).width > 0 ? ((y16 as any).left + (y16 as any).right) / 2 : 0;
    const y17AxisCenter = y17 && (y17 as any).width > 0 ? ((y17 as any).left + (y17 as any).right) / 2 : 0;
    const y18AxisCenter = y18 && (y18 as any).width > 0 ? ((y18 as any).left + (y18 as any).right) / 2 : 0;
    const y19AxisCenter = y19 && (y19 as any).width > 0 ? ((y19 as any).left + (y19 as any).right) / 2 : 0;
    const y20AxisCenter = y20 && (y20 as any).width > 0 ? ((y20 as any).left + (y20 as any).right) / 2 : 0;
    const y21AxisCenter = y21 && (y21 as any).width > 0 ? ((y21 as any).left + (y21 as any).right) / 2 : 0;
    const y22AxisCenter = y22 && (y22 as any).width > 0 ? ((y22 as any).left + (y22 as any).right) / 2 : 0;
    const y23AxisCenter = y23 && (y23 as any).width > 0 ? ((y23 as any).left + (y23 as any).right) / 2 : 0;
    
    
    if (axisPositionCallback) {
      axisPositionCallback({ y: yAxisCenter, y1: y1AxisCenter, y2: y2AxisCenter, y3: y3AxisCenter, y4: y4AxisCenter, y5: y5AxisCenter, y6: y6AxisCenter, y7: y7AxisCenter, y8: y8AxisCenter, y9: y9AxisCenter, y10: y10AxisCenter, y11: y11AxisCenter, y12: y12AxisCenter, y13: y13AxisCenter, y14: y14AxisCenter, y15: y15AxisCenter, y16: y16AxisCenter, y17: y17AxisCenter, y18: y18AxisCenter, y19: y19AxisCenter, y20: y20AxisCenter, y21: y21AxisCenter, y22: y22AxisCenter, y23: y23AxisCenter });
    }

    // Only the chart currently under the pointer should render the crosshair.
    // This prevents the dashed line from "sticking" on a previous chart when
    // another chart updates the shared mouse state.
    if (activeCrosshairChart !== chart) {
      return;
    }
    
    if (mouseX === null || mouseY === null) {
      return;
    }

    const rawXPos = Math.max(left, Math.min(right, mouseX));
    const rawYPos = Math.max(top, Math.min(bottom, mouseY));

    // When hovering over a point marker (the circle), lock the tooltip/crosshair
    // to that exact point so values don't jump while the cursor is still inside
    // the marker radius.
    let xPos = rawXPos;
    let yPos = rawYPos;

    const LOCK_EXTRA_PX = 2;
    let lockedPoint: { x: number; y: number; dist: number } | null = null;

    for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
      const ds: any = chart.data.datasets[datasetIndex];
      const meta: any = chart.getDatasetMeta(datasetIndex);
      const points: any[] | undefined = meta?.data;
      if (!Array.isArray(points) || points.length === 0) continue;

      const pointRadius = typeof ds?.pointRadius === 'number' ? ds.pointRadius : 0;
      const pointHoverRadius = typeof ds?.pointHoverRadius === 'number' ? ds.pointHoverRadius : pointRadius;
      const radius = Math.max(pointRadius, pointHoverRadius) + LOCK_EXTRA_PX;
      if (!(radius > LOCK_EXTRA_PX)) continue;

      // Safety/perf: very dense series typically have pointRadius=0, but guard anyway.
      if (points.length > 1500) continue;

      for (const el of points) {
        const px = (el as any)?.x;
        const py = (el as any)?.y;
        if (!Number.isFinite(px) || !Number.isFinite(py)) continue;

        const dx = px - rawXPos;
        const dy = py - rawYPos;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= radius && (!lockedPoint || dist < lockedPoint.dist)) {
          lockedPoint = { x: px, y: py, dist };
        }
      }
    }

    if (lockedPoint) {
      xPos = lockedPoint.x;
      yPos = lockedPoint.y;
    }

    hoveredSegmentInfo = null;

    // Draw vertical dashed line
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(128, 128, 128, 0.8)';
    ctx.lineWidth = 1;
    ctx.moveTo(xPos, top);
    ctx.lineTo(xPos, bottom);
    ctx.stroke();

    // Draw horizontal dashed line
    ctx.beginPath();
    ctx.moveTo(left, yPos);
    ctx.lineTo(right, yPos);
    ctx.stroke();
    ctx.restore();

    // Get the values from mouse position
    const xValue = x.getValueForPixel(xPos);
    const yValueLeft = y.getValueForPixel(yPos) || 0;
    const yValueRight = y1 ? (y1.getValueForPixel(yPos) || 0) : 0;

    const getTimeMs = (value: unknown): number | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      const d = value instanceof Date ? value : new Date(value as any);
      const t = d.getTime();
      return Number.isFinite(t) ? t : null;
    };
    const cursorXMs = getTimeMs(xValue);
    // Find the closest data point for each dataset
    let snappedPrice: number | null = null;
    let snappedRevenue: number | null = null;
    let snappedNetIncome: number | null = null;
    let snappedGrossProfit: number | null = null;
    let snappedOperatingIncome: number | null = null;
    let snappedPretaxIncome: number | null = null;
    let snappedPE: number | null = null;
    let snappedEps: number | null = null;
    let snappedCash: number | null = null;
    let snappedTotalAssets: number | null = null;
    let snappedShortTermDebt: number | null = null;
    let snappedLongTermDebt: number | null = null;
    let snappedTotalLiabilities: number | null = null;
    let snappedShareholdersEquity: number | null = null;
    let snappedOperatingCashFlow: number | null = null;
    let snappedFreeCashFlow: number | null = null;
    let snappedDividends: number | null = null;
    let snappedBuybacks: number | null = null;
    let snappedGrossProfitMargin: number | null = null;
    let snappedOperatingMargin: number | null = null;
    let snappedNetMargin: number | null = null;
    let snappedOperatingCFMargin: number | null = null;
    let snappedFreeCFMargin: number | null = null;
    let snappedOCFToNetIncome: number | null = null;
    let snappedFCFToNetIncome: number | null = null;
    let snappedROIC: number | null = null;
    let snappedFlatEPSTTM: number | null = null;
    let snappedFlatOCFTTM: number | null = null;
    let snappedFairPrice: number | null = null;
    let snappedFairPriceFlatEPSTTM: number | null = null;
    let snappedEntryPrice: number | null = null;
    let snappedExitPrice: number | null = null;
    let snappedFairPriceOcf: number | null = null;
    let snappedEntryPriceOcf: number | null = null;
    let snappedExitPriceOcf: number | null = null;
    let pricePixelY: number | null = null;
    let revenuePixelY: number | null = null;
    let netIncomePixelY: number | null = null;
    let grossProfitPixelY: number | null = null;
    let operatingIncomePixelY: number | null = null;
    let pretaxIncomePixelY: number | null = null;
    let pePixelY: number | null = null;
    let epsPixelY: number | null = null;
    let cashPixelY: number | null = null;
    let totalAssetsPixelY: number | null = null;
    let shortTermDebtPixelY: number | null = null;
    let longTermDebtPixelY: number | null = null;
    let totalLiabilitiesPixelY: number | null = null;
    let shareholdersEquityPixelY: number | null = null;
    let operatingCashFlowPixelY: number | null = null;
    let freeCashFlowPixelY: number | null = null;
    let dividendsPixelY: number | null = null;
    let buybacksPixelY: number | null = null;
    let grossProfitMarginPixelY: number | null = null;
    let operatingMarginPixelY: number | null = null;
    let netMarginPixelY: number | null = null;
    let operatingCFMarginPixelY: number | null = null;
    let freeCFMarginPixelY: number | null = null;
    let ocfToNetIncomePixelY: number | null = null;
    let fcfToNetIncomePixelY: number | null = null;
    let roicPixelY: number | null = null;
    let flatEPSTTMPixelY: number | null = null;
    let flatOCFTTMPixelY: number | null = null;
    let fairPricePixelY: number | null = null;
    let fairPriceFlatEPSTTMPixelY: number | null = null;
    let entryPricePixelY: number | null = null;
    let exitPricePixelY: number | null = null;
    let fairPriceOcfPixelY: number | null = null;
    let entryPriceOcfPixelY: number | null = null;
    let exitPriceOcfPixelY: number | null = null;
    let flatEPSTTMColor: string | null = null;
    let flatOCFTTMColor: string | null = null;
    let fairPriceColor: string | null = null;
    let fairPriceFlatEPSTTMColor: string | null = null;
    let entryPriceColor: string | null = null;
    let exitPriceColor: string | null = null;
    let fairPriceOcfColor: string | null = null;
    let entryPriceOcfColor: string | null = null;
    let exitPriceOcfColor: string | null = null;

    const hoveredCandidates: Array<NonNullable<HoveredSegmentInfo>> = [];
    
    chart.data.datasets.forEach((dataset: any) => {
      if (!dataset.data || dataset.data.length === 0) return;

      const elementId = typeof dataset.elementId === 'string' ? dataset.elementId : null;
      const label = typeof dataset.label === 'string' ? dataset.label : '';
      const resolvedId = ((): 'flat-eps-ttm' | 'flat-ocf-ttm' | 'fair-price' | 'fair-price-flat-eps-ttm' | 'entry-point-1' | 'entry-point-2' | 'fair-price-ocf' | 'entry-point-1-ocf' | 'entry-point-2-ocf' | null => {
        if (
          elementId === 'flat-eps-ttm' ||
          elementId === 'flat-ocf-ttm' ||
          elementId === 'fair-price' ||
          elementId === 'fair-price-flat-eps-ttm' ||
          elementId === 'entry-point-1' ||
          elementId === 'entry-point-2' ||
          elementId === 'fair-price-ocf' ||
          elementId === 'entry-point-1-ocf' ||
          elementId === 'entry-point-2-ocf'
        ) return elementId;
        if (label === 'Flat EPS (ttm)') return 'flat-eps-ttm';
        if (label === 'Flat OCF/Share (ttm)') return 'flat-ocf-ttm';
        if (label === 'Fair Price') return 'fair-price';
        if (label === 'Fair Price Flat EPS (ttm)') return 'fair-price-flat-eps-ttm';
        if (label === 'Entry Price') return 'entry-point-1';
        if (label === 'Exit Price') return 'entry-point-2';
        return null;
      })();

      // For quarter-segment series, always compute the step value at the current X.
      // This keeps the series tooltips aligned to the actual horizontal segment even
      // when the mouse is above/below the line.
      if (cursorXMs !== null && resolvedId) {
        const yAxisId = typeof dataset.yAxisID === 'string' ? dataset.yAxisID : 'y';
        const yAxis = chart.scales?.[yAxisId];
        if (!yAxis) return;

        const points = dataset.data as any[];

        // For quarter-step series we insert explicit gaps using null points.
        // We prefer a value derived from a drawn segment (finite->finite consecutive points).
        // If the cursor is inside a gap, we keep the tooltip visible by carrying forward
        // the last known finite value at/before the cursor X.
        let earliest: { t: number; y: number } | null = null;
        let lastFiniteBeforeOrAt: { t: number; y: number } | null = null;
        const isLockedEntryExitQuarterSeries =
          dataset.borderWidth === 3 &&
          ['entry-point-1', 'entry-point-2', 'entry-point-1-ocf', 'entry-point-2-ocf'].includes(elementId ?? '');

        for (const p of points) {
          if (!p || typeof p !== 'object') continue;
          const t = getTimeMs((p as any).x);
          if (t === null) continue;

          const yRaw = (p as any).y;
          const yVal = typeof yRaw === 'number' ? yRaw : (typeof yRaw === 'string' ? parseFloat(yRaw) : NaN);
          if (!Number.isFinite(yVal)) continue;

          if (!earliest || t < earliest.t) earliest = { t, y: yVal };
          if (t <= cursorXMs && (!lastFiniteBeforeOrAt || t >= lastFiniteBeforeOrAt.t)) {
            lastFiniteBeforeOrAt = { t, y: yVal };
          }
        }

        if (!earliest) return;

        let yAtCursor: number | null = null;
        for (let i = 0; i < points.length - 1; i++) {
          const p0 = points[i];
          const p1 = points[i + 1];
          if (!p0 || !p1 || typeof p0 !== 'object' || typeof p1 !== 'object') continue;

          const t0 = getTimeMs((p0 as any).x);
          const t1 = getTimeMs((p1 as any).x);
          if (t0 === null || t1 === null) continue;

          const y0raw = (p0 as any).y;
          const y1raw = (p1 as any).y;
          const y0 = typeof y0raw === 'number' ? y0raw : (typeof y0raw === 'string' ? parseFloat(y0raw) : NaN);
          const y1v = typeof y1raw === 'number' ? y1raw : (typeof y1raw === 'string' ? parseFloat(y1raw) : NaN);
          if (!Number.isFinite(y0) || !Number.isFinite(y1v)) continue;

          const minT = Math.min(t0, t1);
          const maxT = Math.max(t0, t1);
          if (cursorXMs < minT || cursorXMs > maxT) continue;

          if (t1 === t0) {
            yAtCursor = y1v;
          } else {
            const alpha = (cursorXMs - t0) / (t1 - t0);
            yAtCursor = y0 + alpha * (y1v - y0);
          }
          break;
        }

        // Entry/Exit values must disappear in Lock when the cursor is in a gap.
        // Other quarter-step series keep their historical carry-forward behavior.
        if (yAtCursor === null && !isLockedEntryExitQuarterSeries && lastFiniteBeforeOrAt) {
          yAtCursor = lastFiniteBeforeOrAt.y;
        }

        // If the cursor is before the first segment, keep the tooltip continuous by
        // using the earliest available value.
        if (yAtCursor === null && !isLockedEntryExitQuarterSeries && cursorXMs < earliest.t) {
          yAtCursor = earliest.y;
        }

        if (yAtCursor === null || !Number.isFinite(yAtCursor)) return;

        const pixelY = yAxis.getPixelForValue(yAtCursor);
        if (resolvedId === 'flat-eps-ttm') {
          snappedFlatEPSTTM = yAtCursor;
          flatEPSTTMPixelY = pixelY;
          flatEPSTTMColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (flatEPSTTMColor ?? 'rgba(249, 115, 22, 1)');
        } else if (resolvedId === 'flat-ocf-ttm') {
          snappedFlatOCFTTM = yAtCursor;
          flatOCFTTMPixelY = pixelY;
          flatOCFTTMColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (flatOCFTTMColor ?? 'rgba(34, 197, 94, 1)');
        } else if (resolvedId === 'fair-price') {
          snappedFairPrice = yAtCursor;
          fairPricePixelY = pixelY;
          fairPriceColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (fairPriceColor ?? 'rgba(139, 92, 246, 1)');
        } else if (resolvedId === 'fair-price-flat-eps-ttm') {
          snappedFairPriceFlatEPSTTM = yAtCursor;
          fairPriceFlatEPSTTMPixelY = pixelY;
          fairPriceFlatEPSTTMColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (fairPriceFlatEPSTTMColor ?? 'rgba(6, 182, 212, 1)');
        } else if (resolvedId === 'entry-point-1') {
          snappedEntryPrice = yAtCursor;
          entryPricePixelY = pixelY;
          entryPriceColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (entryPriceColor ?? 'rgba(16, 185, 129, 1)');
        } else if (resolvedId === 'entry-point-2') {
          snappedExitPrice = yAtCursor;
          exitPricePixelY = pixelY;
          exitPriceColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (exitPriceColor ?? 'rgba(239, 68, 68, 1)');
        } else if (resolvedId === 'fair-price-ocf') {
          snappedFairPriceOcf = yAtCursor;
          fairPriceOcfPixelY = pixelY;
          fairPriceOcfColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (fairPriceOcfColor ?? 'rgba(99, 102, 241, 1)');
        } else if (resolvedId === 'entry-point-1-ocf') {
          snappedEntryPriceOcf = yAtCursor;
          entryPriceOcfPixelY = pixelY;
          entryPriceOcfColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (entryPriceOcfColor ?? 'rgba(14, 165, 233, 1)');
        } else if (resolvedId === 'entry-point-2-ocf') {
          snappedExitPriceOcf = yAtCursor;
          exitPriceOcfPixelY = pixelY;
          exitPriceOcfColor = typeof dataset.borderColor === 'string' ? dataset.borderColor : (exitPriceOcfColor ?? 'rgba(249, 115, 22, 1)');
        }
      }

      // Detect if the cursor is directly on a horizontal segment (not just near a point)
      // for the quarter-step series.
      const quarterStepElementId = elementId;
      const isQuarterStepSeries =
        quarterStepElementId === 'flat-eps-ttm' ||
        quarterStepElementId === 'flat-ocf-ttm' ||
        quarterStepElementId === 'fair-price' ||
        quarterStepElementId === 'fair-price-flat-eps-ttm' ||
        quarterStepElementId === 'entry-point-1' ||
        quarterStepElementId === 'entry-point-2' ||
        quarterStepElementId === 'fair-price-ocf' ||
        quarterStepElementId === 'entry-point-1-ocf' ||
        quarterStepElementId === 'entry-point-2-ocf';
      if (isQuarterStepSeries) {
        const yAxisId = typeof dataset.yAxisID === 'string' ? dataset.yAxisID : 'y';
        const yAxis = chart.scales?.[yAxisId];
        if (yAxis) {
          const points = dataset.data as any[];
          for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[i];
            const p1 = points[i + 1];
            if (!p0 || !p1 || typeof p0 !== 'object' || typeof p1 !== 'object') continue;

            const x0 = (p0 as any).x;
            const y0raw = (p0 as any).y;
            const x1 = (p1 as any).x;
            const y1raw = (p1 as any).y;

            const y0 = typeof y0raw === 'number' ? y0raw : (typeof y0raw === 'string' ? parseFloat(y0raw) : NaN);
            const y1v = typeof y1raw === 'number' ? y1raw : (typeof y1raw === 'string' ? parseFloat(y1raw) : NaN);
            if (!Number.isFinite(y0) || !Number.isFinite(y1v)) continue;

            const px0 = x.getPixelForValue(x0);
            const px1 = x.getPixelForValue(x1);
            const denom = px1 - px0;
            if (!Number.isFinite(denom) || denom === 0) continue;

            const t = (xPos - px0) / denom;
            if (t < 0 || t > 1) continue;

            const py0 = yAxis.getPixelForValue(y0);
            const py1 = yAxis.getPixelForValue(y1v);
            const segPixelY = py0 + t * (py1 - py0);
            const distancePx = Math.abs(segPixelY - yPos);
            if (distancePx <= HOVER_SEGMENT_TOLERANCE_PX) {
              const segValue = y0 + t * (y1v - y0);
              hoveredCandidates.push({
                elementId: quarterStepElementId!,
                label: typeof dataset.label === 'string' ? dataset.label : quarterStepElementId!,
                value: segValue,
                pixelX0: px0,
                pixelX1: px1,
                pixelY: segPixelY,
                color: dataset.borderColor || 'rgba(59, 130, 246, 1)',
                baseLineWidth: typeof dataset.borderWidth === 'number' ? dataset.borderWidth : 2,
                distancePx,
              });
              break;
            }
          }
        }

        // IMPORTANT:
        // For the quarter-step series, we already compute a step-at-X value above.
        // Do NOT run the generic "closest-point" snapping below, because it can
        // overwrite pixelY using the wrong axis (and makes the tooltip jump).
        return;
      }

      // Revenue: snap should change only when crossing the next point (not at midpoint).
      // Implement directional step snapping based on cursor movement.
      const isRevenueDataset =
        dataset.yAxisID === 'y1' &&
        typeof dataset.label === 'string' &&
        dataset.label.includes('Revenue');
      if (isRevenueDataset) {
        const yAxis = y1;
        if (!yAxis) return;

        const movingLeft = lastRevenueMouseX !== null ? xPos < lastRevenueMouseX : false;
        lastRevenueMouseX = xPos;

        const tolerancePx = 0.5;
        let chosen: any = null;
        let chosenPixelX: number | null = null;

        const isFinitePointY = (pt: any): boolean => {
          if (!pt) return false;
          const yRaw = typeof pt === 'object' && pt.y !== undefined ? pt.y : pt;
          const yVal = typeof yRaw === 'number'
            ? yRaw
            : (typeof yRaw === 'string' ? parseFloat(yRaw) : NaN);
          return Number.isFinite(yVal);
        };

        const getFirstFinitePoint = (): any | null => {
          for (const pt of dataset.data) {
            if (isFinitePointY(pt)) return pt;
          }
          return null;
        };

        const getLastFinitePoint = (): any | null => {
          for (let i = dataset.data.length - 1; i >= 0; i--) {
            const pt = dataset.data[i];
            if (isFinitePointY(pt)) return pt;
          }
          return null;
        };

        // Iterate points once to pick the proper step point.
        // IMPORTANT: ignore null/NaN points (we append trailing {xMaxDate, y:null}).
        for (const point of dataset.data) {
          if (!isFinitePointY(point)) continue;
          const pointX = typeof point === 'object' && point.x ? point.x : point;
          const pixelX = x.getPixelForValue(pointX);

          if (movingLeft) {
            // Choose the first point on/after the cursor.
            if (pixelX >= xPos - tolerancePx) {
              chosen = point;
              chosenPixelX = pixelX;
              break;
            }
          } else {
            // Choose the last point on/before the cursor.
            if (pixelX <= xPos + tolerancePx) {
              chosen = point;
              chosenPixelX = pixelX;
            }
          }
        }

        // Fallbacks when cursor is before first point or after last point.
        if (!chosen) {
          chosen = movingLeft ? getFirstFinitePoint() : getLastFinitePoint();
          if (!chosen) return;
          const fallbackX = typeof chosen === 'object' && chosen.x ? chosen.x : chosen;
          chosenPixelX = x.getPixelForValue(fallbackX);
        }

        const pointYRaw = typeof chosen === 'object' && chosen.y !== undefined ? chosen.y : chosen;
        const pointY = typeof pointYRaw === 'number'
          ? pointYRaw
          : (typeof pointYRaw === 'string' ? parseFloat(pointYRaw) : NaN);
        if (!Number.isFinite(pointY)) return;

        const pointX = typeof chosen === 'object' && chosen.x !== undefined ? chosen.x : chosen;
        const pixelY = yAxis.getPixelForValue(pointY);
        const pixelX = chosenPixelX ?? x.getPixelForValue(pointX);
        snappedRevenue = pointY;
        revenuePixelY = pixelY;

        // Draw Revenue marker only when near the snapped point.
        if (Math.abs(pixelX - xPos) < 10) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(pixelX, pixelY, 6, 0, 2 * Math.PI);
          ctx.fillStyle = dataset.borderColor || 'rgba(34, 197, 94, 1)';
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        }

        return;
      }
      
      // For all datasets (except Revenue which has custom logic above):
      // Snap should change only when crossing the next point (not at midpoint).
      // Implement directional step snapping based on cursor movement.
      const movingLeft = lastStepMouseX !== null ? xPos < lastStepMouseX : false;
      lastStepMouseX = xPos;

      const tolerancePx = 0.5;
      let chosenPoint: any = null;
      let chosenPixelX: number | null = null;

      const isFinitePointY = (pt: any): boolean => {
        if (!pt) return false;
        const yRaw = typeof pt === 'object' && pt.y !== undefined ? pt.y : pt;
        const yVal = typeof yRaw === 'number'
          ? yRaw
          : (typeof yRaw === 'string' ? parseFloat(yRaw) : NaN);
        return Number.isFinite(yVal);
      };

      const getFirstFinitePoint = (): any | null => {
        for (const pt of dataset.data) {
          if (isFinitePointY(pt)) return pt;
        }
        return null;
      };

      const getLastFinitePoint = (): any | null => {
        for (let i = dataset.data.length - 1; i >= 0; i--) {
          const pt = dataset.data[i];
          if (isFinitePointY(pt)) return pt;
        }
        return null;
      };

      const isLockedEntryExitDataset =
        dataset.borderWidth === 3 &&
        ['entry-point-1', 'entry-point-2', 'entry-point-1-ocf', 'entry-point-2-ocf'].includes(
          String(dataset.elementId ?? '')
        );

      if (isLockedEntryExitDataset) {
        for (let pointIndex = 0; pointIndex < dataset.data.length - 1; pointIndex += 1) {
          const segmentStart = dataset.data[pointIndex];
          const segmentEnd = dataset.data[pointIndex + 1];
          if (!isFinitePointY(segmentStart) || !isFinitePointY(segmentEnd)) continue;

          const startX = x.getPixelForValue(segmentStart.x);
          const endX = x.getPixelForValue(segmentEnd.x);
          const segmentMinX = Math.min(startX, endX);
          const segmentMaxX = Math.max(startX, endX);
          if (xPos >= segmentMinX - tolerancePx && xPos <= segmentMaxX + tolerancePx) {
            chosenPoint = segmentStart;
            chosenPixelX = xPos;
            break;
          }
        }

        if (!chosenPoint) return;
      }

      for (const point of isLockedEntryExitDataset ? [] : dataset.data) {
        if (!isFinitePointY(point)) continue;
        const pointX = typeof point === 'object' && point.x ? point.x : point;
        const pixelX = x.getPixelForValue(pointX);

        if (movingLeft) {
          // Choose the first point on/after the cursor.
          if (pixelX >= xPos - tolerancePx) {
            chosenPoint = point;
            chosenPixelX = pixelX;
            break;
          }
        } else {
          // Choose the last point on/before the cursor.
          if (pixelX <= xPos + tolerancePx) {
            chosenPoint = point;
            chosenPixelX = pixelX;
          }
        }
      }

      if (!chosenPoint) {
        chosenPoint = movingLeft ? getFirstFinitePoint() : getLastFinitePoint();
        if (!chosenPoint) return;
        const fallbackX = typeof chosenPoint === 'object' && chosenPoint.x ? chosenPoint.x : chosenPoint;
        chosenPixelX = x.getPixelForValue(fallbackX);
      }

      const distanceFromCursorX = chosenPixelX !== null ? Math.abs(chosenPixelX - xPos) : Infinity;

      // If we found a step-snapped point, get its actual value
      if (chosenPoint) {
        const pointYRaw = typeof chosenPoint === 'object' && chosenPoint.y !== undefined ? chosenPoint.y : chosenPoint;
        const pointY = typeof pointYRaw === 'number'
          ? pointYRaw
          : (typeof pointYRaw === 'string' ? parseFloat(pointYRaw) : NaN);

        // Skip gap points (null) or anything non-numeric
        if (!Number.isFinite(pointY)) {
          return;
        }
        const pointX = typeof chosenPoint === 'object' && chosenPoint.x !== undefined ? chosenPoint.x : chosenPoint;
        
        // Determine which Y axis to use
        let yAxis: any = null;
        switch (dataset.yAxisID) {
          case 'y15':
            yAxis = chart.scales.y15;
            break;
          case 'y16':
            yAxis = chart.scales.y16;
            break;
          case 'y22':
            yAxis = chart.scales.y22;
            break;
          case 'y21':
            yAxis = chart.scales.y21;
            break;
          case 'y20':
            yAxis = chart.scales.y20;
            break;
          case 'y19':
            yAxis = chart.scales.y19;
            break;
          case 'y14':
            yAxis = chart.scales.y14;
            break;
          case 'y13':
            yAxis = chart.scales.y13;
            break;
          case 'y12':
            yAxis = chart.scales.y12;
            break;
          case 'y11':
            yAxis = chart.scales.y11;
            break;
          case 'y10':
            yAxis = chart.scales.y10;
            break;
          case 'y9':
            yAxis = chart.scales.y9;
            break;
          case 'y8':
            yAxis = chart.scales.y8;
            break;
          case 'y7':
            yAxis = chart.scales.y7;
            break;
          case 'y6':
            yAxis = chart.scales.y6;
            break;
          case 'y5':
            yAxis = chart.scales.y5;
            break;
          case 'y4':
            yAxis = chart.scales.y4;
            break;
          case 'y3':
            yAxis = chart.scales.y3;
            break;
          case 'y2':
            yAxis = chart.scales.y2;
            break;
          case 'y1':
            yAxis = y1;
            break;
          default:
            yAxis = y;
            break;
        }
        // Skip if the required axis doesn't exist
        if (!yAxis) {
          return;
        }
        
        const pixelY = yAxis.getPixelForValue(pointY);
        const pixelX = chosenPixelX ?? x.getPixelForValue(pointX);
        
        // Store the snapped exact value from the data point AND its pixel position
        const label = typeof dataset.label === 'string' ? dataset.label : '';
        if (label === 'Flat EPS (ttm)') {
          snappedFlatEPSTTM = pointY;
          flatEPSTTMPixelY = pixelY;
        } else if (label === 'Fair Price') {
          snappedFairPrice = pointY;
          fairPricePixelY = pixelY;
        } else if (label === 'Fair Price Flat EPS (ttm)') {
          snappedFairPriceFlatEPSTTM = pointY;
          fairPriceFlatEPSTTMPixelY = pixelY;
        } else if (dataset.yAxisID === 'y21') {
          snappedROIC = pointY;
          roicPixelY = pixelY;
        } else if (dataset.yAxisID === 'y15') {
          snappedFlatOCFTTM = pointY;
          flatOCFTTMPixelY = pixelY;
        } else if (dataset.yAxisID === 'y16') {
          const elementId = typeof dataset.elementId === 'string' ? dataset.elementId : '';
          if (elementId === 'fair-price-ocf') {
            snappedFairPriceOcf = pointY;
            fairPriceOcfPixelY = pixelY;
          } else if (elementId === 'entry-point-1-ocf') {
            snappedEntryPriceOcf = pointY;
            entryPriceOcfPixelY = pixelY;
          } else if (elementId === 'entry-point-2-ocf') {
            snappedExitPriceOcf = pointY;
            exitPriceOcfPixelY = pixelY;
          }
        } else if (dataset.yAxisID === 'y20') {
          snappedFCFToNetIncome = pointY;
          fcfToNetIncomePixelY = pixelY;
        } else if (dataset.yAxisID === 'y19') {
          snappedOCFToNetIncome = pointY;
          ocfToNetIncomePixelY = pixelY;
        } else if (dataset.yAxisID === 'y14') {
          // y14 hosts all margin series; disambiguate by label
          const label = typeof dataset.label === 'string' ? dataset.label : '';
          if (label === 'Gross Profit Margin') {
            snappedGrossProfitMargin = pointY;
            grossProfitMarginPixelY = pixelY;
          } else if (label === 'Operating Margin') {
            snappedOperatingMargin = pointY;
            operatingMarginPixelY = pixelY;
          } else if (label === 'Net Margin') {
            snappedNetMargin = pointY;
            netMarginPixelY = pixelY;
          } else if (label === 'Operating Cash Flow Margin') {
            snappedOperatingCFMargin = pointY;
            operatingCFMarginPixelY = pixelY;
          } else if (label === 'Free Cash Flow Margin') {
            snappedFreeCFMargin = pointY;
            freeCFMarginPixelY = pixelY;
          }
        } else if (dataset.yAxisID === 'y13') {
          snappedBuybacks = pointY;
          buybacksPixelY = pixelY;
        } else if (dataset.yAxisID === 'y12') {
          snappedDividends = pointY;
          dividendsPixelY = pixelY;
        } else if (dataset.yAxisID === 'y10') {
          // y10 hosts both Operating Cash Flow and Free Cash Flow
          const label = typeof dataset.label === 'string' ? dataset.label : '';
          const isFreeCashFlow = label === 'Free Cash Flow' || label === 'Free Cash Flow Margin';
          if (isFreeCashFlow) {
            snappedFreeCashFlow = pointY;
            freeCashFlowPixelY = pixelY;
          } else {
            snappedOperatingCashFlow = pointY;
            operatingCashFlowPixelY = pixelY;
          }
        } else if (dataset.yAxisID === 'y9') {
          snappedShareholdersEquity = pointY;
          shareholdersEquityPixelY = pixelY;
        } else if (dataset.yAxisID === 'y8') {
          snappedTotalLiabilities = pointY;
          totalLiabilitiesPixelY = pixelY;
        } else if (dataset.yAxisID === 'y7') {
          snappedLongTermDebt = pointY;
          longTermDebtPixelY = pixelY;
        } else if (dataset.yAxisID === 'y6') {
          snappedShortTermDebt = pointY;
          shortTermDebtPixelY = pixelY;
        } else if (dataset.yAxisID === 'y5') {
          snappedTotalAssets = pointY;
          totalAssetsPixelY = pixelY;
        } else if (dataset.yAxisID === 'y4') {
          snappedCash = pointY;
          cashPixelY = pixelY;
        } else if (dataset.yAxisID === 'y3') {
          snappedEps = pointY;
          epsPixelY = pixelY;
        } else if (dataset.yAxisID === 'y2') {
          snappedPE = pointY;
          pePixelY = pixelY;
        } else if (dataset.yAxisID === 'y1') {
          // Check if this is revenue, gross profit, operating income, pretax income, or net income by label
          if (dataset.label && dataset.label.includes('Net Income')) {
            snappedNetIncome = pointY;
            netIncomePixelY = pixelY;
          } else if (dataset.label && dataset.label.includes('Pretax Income')) {
            snappedPretaxIncome = pointY;
            pretaxIncomePixelY = pixelY;
          } else if (dataset.label && dataset.label.includes('Operating Income')) {
            snappedOperatingIncome = pointY;
            operatingIncomePixelY = pixelY;
          } else if (dataset.label && dataset.label.includes('Gross Profit')) {
            snappedGrossProfit = pointY;
            grossProfitPixelY = pixelY;
          }
        } else {
          snappedPrice = pointY;
          pricePixelY = pixelY;
        }
        
        // Draw the circle marker for all datasets when hovering near the snapped point
        if (distanceFromCursorX < 10) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(pixelX, pixelY, 6, 0, 2 * Math.PI);
          ctx.fillStyle = dataset.borderColor || 'rgba(59, 130, 246, 1)';
          ctx.fill();
          // Use black stroke for PE, white for others
          ctx.strokeStyle = dataset.label === 'PE' ? 'black' : 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        }
      }
    });

    // Choose the best hovered segment (closest in Y; stable priority when overlapping)
    if (hoveredCandidates.length > 0) {
      const priority = (id: string) => {
        if (id === 'flat-eps-ttm') return 0;
        if (id === 'flat-ocf-ttm') return 1;
        if (id === 'fair-price') return 2;
        if (id === 'fair-price-flat-eps-ttm') return 3;
        if (id === 'entry-point-1') return 4;
        if (id === 'entry-point-2') return 5;
        if (id === 'fair-price-ocf') return 6;
        if (id === 'entry-point-1-ocf') return 7;
        if (id === 'entry-point-2-ocf') return 8;
        return 99;
      };
      hoveredSegmentInfo = hoveredCandidates.sort((a, b) => {
        if (a.distancePx !== b.distancePx) return a.distancePx - b.distancePx;
        return priority(a.elementId) - priority(b.elementId);
      })[0];

      // When the cursor is truly on a segment, prefer that exact value.
      if (hoveredSegmentInfo.elementId === 'flat-eps-ttm') {
        snappedFlatEPSTTM = hoveredSegmentInfo.value;
        flatEPSTTMPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'flat-ocf-ttm') {
        snappedFlatOCFTTM = hoveredSegmentInfo.value;
        flatOCFTTMPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'fair-price-ocf') {
        snappedFairPriceOcf = hoveredSegmentInfo.value;
        fairPriceOcfPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'entry-point-1-ocf') {
        snappedEntryPriceOcf = hoveredSegmentInfo.value;
        entryPriceOcfPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'entry-point-2-ocf') {
        snappedExitPriceOcf = hoveredSegmentInfo.value;
        exitPriceOcfPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'fair-price') {
        snappedFairPrice = hoveredSegmentInfo.value;
        fairPricePixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'fair-price-flat-eps-ttm') {
        snappedFairPriceFlatEPSTTM = hoveredSegmentInfo.value;
        fairPriceFlatEPSTTMPixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'entry-point-1') {
        snappedEntryPrice = hoveredSegmentInfo.value;
        entryPricePixelY = hoveredSegmentInfo.pixelY;
      } else if (hoveredSegmentInfo.elementId === 'entry-point-2') {
        snappedExitPrice = hoveredSegmentInfo.value;
        exitPricePixelY = hoveredSegmentInfo.pixelY;
      }
    }

    // Draw overlay highlight for the hovered segment only.
    if (hoveredSegmentInfo) {
      const x0 = hoveredSegmentInfo.pixelX0;
      const x1 = hoveredSegmentInfo.pixelX1;
      const yPx = hoveredSegmentInfo.pixelY;
      ctx.save();
      ctx.setLineDash([]);
      ctx.strokeStyle = hoveredSegmentInfo.color;
      ctx.lineWidth = hoveredSegmentInfo.baseLineWidth + 1;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(x0, yPx);
      ctx.lineTo(x1, yPx);
      ctx.stroke();
      ctx.restore();
    }

    // Draw y22 markers at the snapped series value(s) for the current X.
    // This visually connects the right-axis tooltip(s) to the actual horizontal segment,
    // even when the mouse Y is far above/below the line.
    const drawSeriesMarker = (pixelY: number | null, color: string | null) => {
      if (typeof pixelY !== 'number' || !Number.isFinite(pixelY)) return;
      const clampedY = Math.max(top, Math.min(bottom, pixelY));
      ctx.save();
      ctx.beginPath();
      ctx.arc(xPos, clampedY, 4, 0, 2 * Math.PI);
      ctx.fillStyle = color ?? 'rgba(75, 85, 99, 1)';
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    };

    drawSeriesMarker(flatEPSTTMPixelY, flatEPSTTMColor);
    drawSeriesMarker(flatOCFTTMPixelY, flatOCFTTMColor);
    drawSeriesMarker(fairPricePixelY, fairPriceColor);
    drawSeriesMarker(fairPriceFlatEPSTTMPixelY, fairPriceFlatEPSTTMColor);
    drawSeriesMarker(entryPricePixelY, entryPriceColor);
    drawSeriesMarker(exitPricePixelY, exitPriceColor);
    
    if (tooltipCallback && xValue !== null && xValue !== undefined) {
      const date = new Date(xValue as any);
      
      // Get Y-axis values for dynamic tooltip on axes - using values already calculated above
      const yValuePE = chart.scales.y2 ? chart.scales.y2.getValueForPixel(yPos) || 0 : 0;
      const yValueEps = chart.scales.y3 ? chart.scales.y3.getValueForPixel(yPos) || 0 : 0;
      const yValueCash = chart.scales.y4 ? chart.scales.y4.getValueForPixel(yPos) || 0 : 0;
      const yValueTotalAssets = chart.scales.y5 ? chart.scales.y5.getValueForPixel(yPos) || 0 : 0;
      const yValueShortTermDebt = chart.scales.y6 ? chart.scales.y6.getValueForPixel(yPos) || 0 : 0;
      const yValueLongTermDebt = chart.scales.y7 ? chart.scales.y7.getValueForPixel(yPos) || 0 : 0;
      const yValueTotalLiabilities = chart.scales.y8 ? chart.scales.y8.getValueForPixel(yPos) || 0 : 0;
      const yValueShareholdersEquity = chart.scales.y9 ? chart.scales.y9.getValueForPixel(yPos) || 0 : 0;
      const yValueOperatingCashFlow = chart.scales.y10 ? chart.scales.y10.getValueForPixel(yPos) || 0 : 0;
      const yValueFreeCashFlow = chart.scales.y10 ? chart.scales.y10.getValueForPixel(yPos) || 0 : 0;
      const yValueDividends = chart.scales.y12 ? chart.scales.y12.getValueForPixel(yPos) || 0 : 0;
      const yValueBuybacks = chart.scales.y13 ? chart.scales.y13.getValueForPixel(yPos) || 0 : 0;
      // All margin series share y14
      const yValueGrossProfitMargin = chart.scales.y14 ? chart.scales.y14.getValueForPixel(yPos) || 0 : 0;
      const yValueOperatingMargin = chart.scales.y14 ? chart.scales.y14.getValueForPixel(yPos) || 0 : 0;
      const yValueNetMargin = chart.scales.y14 ? chart.scales.y14.getValueForPixel(yPos) || 0 : 0;
      const yValueOperatingCFMargin = chart.scales.y14 ? chart.scales.y14.getValueForPixel(yPos) || 0 : 0;
      const yValueFreeCFMargin = chart.scales.y14 ? chart.scales.y14.getValueForPixel(yPos) || 0 : 0;
      const yValueOCFToNetIncome = chart.scales.y19 ? chart.scales.y19.getValueForPixel(yPos) || 0 : 0;
      const yValueFCFToNetIncome = chart.scales.y20 ? chart.scales.y20.getValueForPixel(yPos) || 0 : 0;
      const yValueROIC = chart.scales.y21 ? chart.scales.y21.getValueForPixel(yPos) || 0 : 0;
      // Flat EPS axis is cloned from EPS axis; prefer y3 when present to guarantee identical readouts.
      const yValueEPSTTM = chart.scales.y3
        ? chart.scales.y3.getValueForPixel(yPos) || 0
        : (chart.scales.y22 ? chart.scales.y22.getValueForPixel(yPos) || 0 : 0);
      const yValueFlatOCFTTM = chart.scales.y15 ? chart.scales.y15.getValueForPixel(yPos) || 0 : 0;
      const hasVisiblePriceDataset = (chart.data?.datasets ?? []).some((ds: any, index: number) => {
        if (ds?.elementId !== 'price-line') return false;
        const meta = chart.getDatasetMeta?.(index);
        return !!meta && !meta.hidden;
      });
      const ocfPriceAxis = hasVisiblePriceDataset ? chart.scales.y : chart.scales.y16;
      const yValueFairPriceOcf = ocfPriceAxis ? ocfPriceAxis.getValueForPixel(yPos) || 0 : 0;
      const yValueFairPrice = chart.scales.y23 ? chart.scales.y23.getValueForPixel(yPos) || 0 : yValueLeft;
      
      // Send BOTH types of values:
      // - Y-axis values (for dynamic tooltip on axes)
      // - Actual graph data values (for static tooltip-no-data)
      tooltipCallback({
        // Use ISO so display formatting is handled in the React UI (locale-safe).
        date: date.toISOString(),
        // Y-axis values (what the mouse is pointing at on the axes)
        yAxisPrice: yValueLeft,
        yAxisFairPrice: yValueFairPrice,
        yAxisRevenue: yValueRight,
        yAxisPE: yValuePE,
        yAxisEps: yValueEps,
        yAxisCash: yValueCash,
        yAxisTotalAssets: yValueTotalAssets,
        yAxisShortTermDebt: yValueShortTermDebt,
        yAxisLongTermDebt: yValueLongTermDebt,
        yAxisTotalLiabilities: yValueTotalLiabilities,
        yAxisShareholdersEquity: yValueShareholdersEquity,
        yAxisOperatingCashFlow: yValueOperatingCashFlow,
        yAxisFreeCashFlow: yValueFreeCashFlow,
        yAxisDividends: yValueDividends,
        yAxisBuybacks: yValueBuybacks,
        yAxisGrossProfitMargin: yValueGrossProfitMargin,
        yAxisOperatingMargin: yValueOperatingMargin,
        yAxisNetMargin: yValueNetMargin,
        yAxisOperatingCFMargin: yValueOperatingCFMargin,
        yAxisFreeCFMargin: yValueFreeCFMargin,
        yAxisOCFToNetIncome: yValueOCFToNetIncome,
        yAxisFCFToNetIncome: yValueFCFToNetIncome,
        yAxisROIC: yValueROIC,
        yAxisFlatEPSTTM: yValueEPSTTM,
        yAxisFlatOCFTTM: yValueFlatOCFTTM,
        yAxisFairPriceOCF: yValueFairPriceOcf,
        // Actual graph data values (from the closest data point)
        graphPrice: snappedPrice !== null ? snappedPrice : 0,
        graphRevenue: snappedRevenue !== null ? snappedRevenue : 0,
        graphNetIncome: snappedNetIncome !== null ? snappedNetIncome : 0,
        graphGrossProfit: snappedGrossProfit !== null ? snappedGrossProfit : 0,
        graphOperatingIncome: snappedOperatingIncome !== null ? snappedOperatingIncome : 0,
        graphPretaxIncome: snappedPretaxIncome !== null ? snappedPretaxIncome : 0,
        graphPE: snappedPE !== null ? snappedPE : 0,
        graphEps: snappedEps !== null ? snappedEps : 0,
        graphCash: snappedCash !== null ? snappedCash : 0,
        graphTotalAssets: snappedTotalAssets !== null ? snappedTotalAssets : 0,
        graphShortTermDebt: snappedShortTermDebt !== null ? snappedShortTermDebt : 0,
        graphLongTermDebt: snappedLongTermDebt !== null ? snappedLongTermDebt : 0,
        graphTotalLiabilities: snappedTotalLiabilities !== null ? snappedTotalLiabilities : 0,
        graphShareholdersEquity: snappedShareholdersEquity !== null ? snappedShareholdersEquity : 0,
        graphOperatingCashFlow: snappedOperatingCashFlow !== null ? snappedOperatingCashFlow : 0,
        graphFreeCashFlow: snappedFreeCashFlow !== null ? snappedFreeCashFlow : 0,
        graphDividends: snappedDividends !== null ? snappedDividends : 0,
        graphBuybacks: snappedBuybacks !== null ? snappedBuybacks : 0,
        graphGrossProfitMargin: snappedGrossProfitMargin !== null ? snappedGrossProfitMargin : 0,
        graphOperatingMargin: snappedOperatingMargin !== null ? snappedOperatingMargin : 0,
        graphNetMargin: snappedNetMargin !== null ? snappedNetMargin : 0,
        graphOperatingCFMargin: snappedOperatingCFMargin !== null ? snappedOperatingCFMargin : 0,
        graphFreeCFMargin: snappedFreeCFMargin !== null ? snappedFreeCFMargin : 0,
        graphOCFToNetIncome: snappedOCFToNetIncome !== null ? snappedOCFToNetIncome : 0,
        graphFCFToNetIncome: snappedFCFToNetIncome !== null ? snappedFCFToNetIncome : 0,
        graphROIC: snappedROIC !== null ? snappedROIC : 0,
        graphFlatEPSTTM: snappedFlatEPSTTM !== null ? snappedFlatEPSTTM : 0,
        graphFlatOCFTTM: snappedFlatOCFTTM !== null ? snappedFlatOCFTTM : 0,
        graphFairPrice: snappedFairPrice !== null ? snappedFairPrice : 0,
        graphFairPriceFlatEPSTTM: snappedFairPriceFlatEPSTTM !== null ? snappedFairPriceFlatEPSTTM : 0,
        graphEntryPrice: snappedEntryPrice !== null ? snappedEntryPrice : 0,
        graphExitPrice: snappedExitPrice !== null ? snappedExitPrice : 0,
        graphFairPriceOCF: snappedFairPriceOcf !== null ? snappedFairPriceOcf : 0,
        graphEntryPriceOCF: snappedEntryPriceOcf !== null ? snappedEntryPriceOcf : 0,
        graphExitPriceOCF: snappedExitPriceOcf !== null ? snappedExitPriceOcf : 0,
        hoveredElementId: hoveredSegmentInfo?.elementId ?? null,
        hoveredElementLabel: hoveredSegmentInfo?.label ?? null,
        hoveredElementValue: hoveredSegmentInfo?.value ?? null,
        chartArea: { left, right, top, bottom },
        mouseX: xPos,
        mouseY: yPos,
        pricePixelY,
        revenuePixelY,
        netIncomePixelY,
        grossProfitPixelY,
        operatingIncomePixelY,
        pretaxIncomePixelY,
        pePixelY,
        epsPixelY,
        cashPixelY,
        totalAssetsPixelY,
        shortTermDebtPixelY,
        longTermDebtPixelY,
        totalLiabilitiesPixelY,
        shareholdersEquityPixelY,
        operatingCashFlowPixelY,
        freeCashFlowPixelY,
        dividendsPixelY,
        buybacksPixelY,
        grossProfitMarginPixelY,
        operatingMarginPixelY,
        netMarginPixelY,
        operatingCFMarginPixelY,
        freeCFMarginPixelY,
        ocfToNetIncomePixelY,
        fcfToNetIncomePixelY,
        roicPixelY,
        flatEPSTTMPixelY,
        flatOCFTTMPixelY,
        fairPricePixelY,
        fairPriceFlatEPSTTMPixelY,
        entryPricePixelY,
        exitPricePixelY,
        fairPriceOcfPixelY,
        entryPriceOcfPixelY,
        exitPriceOcfPixelY
      });
    }
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  crosshairPlugin
);

export interface ChartElement {
  id: string;
  name: string;
  type: 'line' | 'entry-point' | 'support' | 'resistance';
  data: Array<{x: string | Date | number, y: number}> | number[]; // Support both {x, y} format and simple numbers
  color: string;
  visible: boolean;
  adjustment: number; // percentage adjustment (-100 to 100)
  useTimeScale?: boolean; // Flag to indicate if this dataset uses time scale
  stepped?: boolean; // Flag for stepped line (flat/horizontal segments)
  orderIndex?: number; // Order in which element was made visible (for tooltip sorting)
}

export type ChartAreaInsets = {
  leftInsetPx: number;
  rightInsetPx: number;
  topInsetPx: number;
  bottomInsetPx: number;
  canvasWidthPx: number;
  canvasHeightPx: number;
};

interface AdvancedChartProps {
  elements: ChartElement[];
  labels: string[]; // Still needed for backwards compatibility
  isDarkMode: boolean;
  chartsDebug?: boolean;
  xMin?: string | Date | number;
  xMax?: string | Date | number;
  normalizedPEOverlayValue?: number | null;
  normalizedPOCFOverlayValue?: number | null;
  isDateRangeLocked?: boolean;
  onTooltipChange?: (data: AdvancedChartTooltipData | null) => void;
  onChartAreaInsetsChange?: (insets: ChartAreaInsets) => void;
}

const AdvancedChart: React.FC<AdvancedChartProps> = ({ elements, labels, isDarkMode, chartsDebug, xMin, xMax, normalizedPEOverlayValue, normalizedPOCFOverlayValue, isDateRangeLocked = false, onTooltipChange, onChartAreaInsetsChange }) => {
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';
  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
  const backgroundColor = isDarkMode ? '#111827' : '#ffffff';
  const tooltipBackgroundColor = isDarkMode ? '#111827' : 'rgba(75, 85, 99, 0.98)';
  const axisBorder = { display: true, width: 2, color: gridColor };

  const xMaxDate = React.useMemo(() => {
    if (!xMax) return null;
    const d = new Date(xMax);
    return Number.isFinite(d.getTime()) ? d : null;
  }, [xMax]);
  
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const chartRef = React.useRef<any>(null);
  const normalizedPEOverlayValueRef = React.useRef<number | null>(normalizedPEOverlayValue ?? null);
  const normalizedPOCFOverlayValueRef = React.useRef<number | null>(normalizedPOCFOverlayValue ?? null);
  const [axisPositions, setAxisPositions] = React.useState({ y: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0, y6: 0, y7: 0, y8: 0, y9: 0, y10: 0, y11: 0, y12: 0, y13: 0, y14: 0, y15: 0, y16: 0, y17: 0, y18: 0, y19: 0, y20: 0, y21: 0, y22: 0, y23: 0 });
  const [normalizedPEPixelY, setNormalizedPEPixelY] = React.useState<number | null>(null);
  const [normalizedPOCFPixelY, setNormalizedPOCFPixelY] = React.useState<number | null>(null);
  const [lastValuePositions, setLastValuePositions] = React.useState<{ price: number | null; revenue: number | null; grossProfit: number | null; operatingIncome: number | null; pretaxIncome: number | null; netIncome: number | null; pe: number | null; pOcf: number | null; pFcf: number | null; eps: number | null; cash: number | null; totalAssets: number | null; shortTermDebt: number | null; longTermDebt: number | null; totalLiabilities: number | null; shareholdersEquity: number | null; operatingCashFlow: number | null; freeCashFlow: number | null; dividends: number | null; buybacks: number | null; grossProfitMargin: number | null; operatingMargin: number | null; netMargin: number | null; operatingCFMargin: number | null; freeCFMargin: number | null; ocfToNetIncome: number | null; fcfToNetIncome: number | null; roic: number | null; flatEPSTTM: number | null; flatOCFTTM: number | null; fairPrice: number | null; fairPriceFlatEPSTTM: number | null; entryPrice: number | null; exitPrice: number | null; fairPriceOcf: number | null; entryPriceOcf: number | null; exitPriceOcf: number | null }>({ price: null, revenue: null, grossProfit: null, operatingIncome: null, pretaxIncome: null, netIncome: null, pe: null, pOcf: null, pFcf: null, eps: null, cash: null, totalAssets: null, shortTermDebt: null, longTermDebt: null, totalLiabilities: null, shareholdersEquity: null, operatingCashFlow: null, freeCashFlow: null, dividends: null, buybacks: null, grossProfitMargin: null, operatingMargin: null, netMargin: null, operatingCFMargin: null, freeCFMargin: null, ocfToNetIncome: null, fcfToNetIncome: null, roic: null, flatEPSTTM: null, flatOCFTTM: null, fairPrice: null, fairPriceFlatEPSTTM: null, entryPrice: null, exitPrice: null, fairPriceOcf: null, entryPriceOcf: null, exitPriceOcf: null });
  const fixedChartDimensions = React.useRef({ top: 10, bottom: 0, height: 0 });
  const onTooltipChangeRef = React.useRef(onTooltipChange);
  const onChartAreaInsetsChangeRef = React.useRef(onChartAreaInsetsChange);
  const lastReportedInsetsRef = React.useRef<ChartAreaInsets | null>(null);

  const axisHasBillionsTick = (axisId: string): boolean => {
    const chart = chartRef.current || globalChartInstance;
    const ticks = chart?.scales?.[axisId]?.ticks as Array<unknown> | undefined;
    if (!Array.isArray(ticks)) return false;

    return ticks.some((tick) => {
      const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
      const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
      return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
    });
  };

  const formatRevenueForAxis = (value: number, axisId: string): string => {
    if (!Number.isFinite(value)) return String(value);
    if (value === 0 && axisHasBillionsTick(axisId)) return '0.00B';
    return formatRevenueWithUnits(value);
  };

  React.useEffect(() => {
    onChartAreaInsetsChangeRef.current = onChartAreaInsetsChange;
  }, [onChartAreaInsetsChange]);

  const chartAreaInsetsReporterPlugin = React.useMemo<Plugin<'line'>>(() => {
    const thresholdPx = 0.5;
    const report = (chart: any) => {
      const area = chart?.chartArea;
      if (!area) return;
      const canvasWidthPx = typeof chart?.width === 'number' ? chart.width : 0;
      const canvasHeightPx = typeof chart?.height === 'number' ? chart.height : 0;
      if (canvasWidthPx <= 0 || canvasHeightPx <= 0) return;

      const next: ChartAreaInsets = {
        leftInsetPx: area.left,
        rightInsetPx: canvasWidthPx - area.right,
        topInsetPx: area.top,
        bottomInsetPx: canvasHeightPx - area.bottom,
        canvasWidthPx,
        canvasHeightPx,
      };

      const prev = lastReportedInsetsRef.current;
      const unchanged =
        prev &&
        Math.abs(prev.leftInsetPx - next.leftInsetPx) < thresholdPx &&
        Math.abs(prev.rightInsetPx - next.rightInsetPx) < thresholdPx &&
        Math.abs(prev.topInsetPx - next.topInsetPx) < thresholdPx &&
        Math.abs(prev.bottomInsetPx - next.bottomInsetPx) < thresholdPx &&
        Math.abs(prev.canvasWidthPx - next.canvasWidthPx) < thresholdPx &&
        Math.abs(prev.canvasHeightPx - next.canvasHeightPx) < thresholdPx;
      if (unchanged) return;

      lastReportedInsetsRef.current = next;
      onChartAreaInsetsChangeRef.current?.(next);
    };

    return {
      id: 'chartAreaInsetsReporter',
      afterLayout(chart: any) {
        report(chart);
      },
      resize(chart: any) {
        report(chart);
      },
    };
  }, []);

  const tooltipDataRef = React.useRef<AdvancedChartTooltipData | null>(null);

  const [tooltipData, setTooltipData] = React.useState<AdvancedChartTooltipData | null>(null);
  
  // Keep ref up to date
  React.useEffect(() => {
    onTooltipChangeRef.current = onTooltipChange;
  }, [onTooltipChange]);

  React.useEffect(() => {
    normalizedPEOverlayValueRef.current = normalizedPEOverlayValue ?? null;
  }, [normalizedPEOverlayValue]);

  React.useEffect(() => {
    normalizedPOCFOverlayValueRef.current = normalizedPOCFOverlayValue ?? null;
  }, [normalizedPOCFOverlayValue]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    // The chart can mount while visibility is temporarily suppressed during tab
    // hydration. Retry after layout so draw-only overlays are restored reliably.
    let cancelled = false;
    let raf1: number | null = null;
    let raf2: number | null = null;

    const redraw = () => {
      if (cancelled) return;
      const currentChart = chartRef.current;
      if (!currentChart) return;
      currentChart.update('none');
      currentChart.draw();
    };

    redraw();
    raf1 = requestAnimationFrame(() => {
      redraw();
      raf2 = requestAnimationFrame(redraw);
    });

    return () => {
      cancelled = true;
      if (raf1 !== null) cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
    };
  }, [elements, normalizedPEOverlayValue, normalizedPOCFOverlayValue]);
  
  // Set the tooltip callback
  React.useEffect(() => {
    let rafId: number | null = null;
    
    tooltipCallback = (data) => {
      // Store in ref immediately (no re-render)
      tooltipDataRef.current = data;
      
      // Debounce state updates to avoid excessive re-renders
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        setTooltipData(data);
        
        if (onTooltipChangeRef.current) {
          onTooltipChangeRef.current(data);
        }
        
        // Update chart dimensions when we get chartArea - only once to avoid tooltip jumps
        if (data?.chartArea && fixedChartDimensions.current.height === 0) {
          const dims = {
            top: data.chartArea.top,
            bottom: data.chartArea.bottom,
            height: data.chartArea.bottom - data.chartArea.top
          };
          fixedChartDimensions.current = dims;
        }
      });
    };
    
    axisPositionCallback = (positions) => {
      const chart: any = chartRef.current;
      const container = chartContainerRef.current;
      const canvas: HTMLCanvasElement | undefined = chart?.canvas;
      const xOffsetPx = (() => {
        if (!container || !canvas) return 0;
        const containerRect = container.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const dx = canvasRect.left - containerRect.left;
        return Number.isFinite(dx) ? dx : 0;
      })();

      const adjust = (value: unknown): number => {
        if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
        return value + xOffsetPx;
      };

      const adjusted = {
        y: adjust(positions.y),
        y1: adjust(positions.y1),
        y2: adjust(positions.y2),
        y3: adjust(positions.y3),
        y4: adjust(positions.y4),
        y5: adjust(positions.y5),
        y6: adjust(positions.y6),
        y7: adjust(positions.y7),
        y8: adjust(positions.y8),
        y9: adjust(positions.y9),
        y10: adjust(positions.y10),
        y11: adjust(positions.y11),
        y12: adjust(positions.y12),
        y13: adjust(positions.y13),
        y14: adjust(positions.y14),
        y15: adjust(positions.y15),
        y16: adjust(positions.y16),
        y17: adjust(positions.y17),
        y18: adjust(positions.y18),
        y19: adjust(positions.y19),
        y20: adjust(positions.y20),
        y21: adjust(positions.y21),
        y22: adjust(positions.y22),
        y23: adjust(positions.y23),
      };

      setAxisPositions(prev => {
        // Only update if values changed by more than 0.5px (avoid floating point issues)
        const threshold = 0.5;
        if (Math.abs(prev.y - adjusted.y) < threshold && 
            Math.abs(prev.y1 - adjusted.y1) < threshold && 
            Math.abs(prev.y2 - adjusted.y2) < threshold &&
            Math.abs(prev.y3 - adjusted.y3) < threshold &&
            Math.abs(prev.y4 - adjusted.y4) < threshold &&
            Math.abs(prev.y5 - adjusted.y5) < threshold &&
            Math.abs(prev.y6 - adjusted.y6) < threshold &&
            Math.abs(prev.y7 - adjusted.y7) < threshold &&
            Math.abs(prev.y8 - adjusted.y8) < threshold &&
            Math.abs(prev.y9 - adjusted.y9) < threshold &&
            Math.abs(prev.y10 - adjusted.y10) < threshold &&
            Math.abs(prev.y11 - adjusted.y11) < threshold &&
            Math.abs(prev.y12 - adjusted.y12) < threshold &&
            Math.abs(prev.y13 - adjusted.y13) < threshold &&
            Math.abs(prev.y14 - adjusted.y14) < threshold &&
            Math.abs(prev.y15 - adjusted.y15) < threshold &&
            Math.abs(prev.y16 - adjusted.y16) < threshold &&
            Math.abs(prev.y17 - adjusted.y17) < threshold &&
            Math.abs(prev.y18 - adjusted.y18) < threshold &&
            Math.abs(prev.y19 - adjusted.y19) < threshold &&
            Math.abs(prev.y20 - adjusted.y20) < threshold &&
            Math.abs(prev.y21 - adjusted.y21) < threshold &&
            Math.abs(prev.y22 - adjusted.y22) < threshold &&
            Math.abs(prev.y23 - adjusted.y23) < threshold) {
          return prev;
        }
        return adjusted;
      });
    };
    
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      tooltipCallback = null;
      axisPositionCallback = null;
    };
  }, []); // Empty dependency array - use ref for onTooltipChange

  // Calculate pixel positions for last values using chart scales
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const calculateLastValuePositions = () => {
      const xMinVisible = typeof effectiveXBounds?.min === 'number' ? effectiveXBounds.min : Number.NEGATIVE_INFINITY;
      const xMaxVisible = typeof effectiveXBounds?.max === 'number' ? effectiveXBounds.max : Number.POSITIVE_INFINITY;

      const getLastFiniteYInVisibleRange = (data: any[]): number | null => {
        for (let i = data.length - 1; i >= 0; i--) {
          const p = data[i];
          const y = (p && typeof p === 'object' && 'y' in p) ? (p as any).y : p;
          if (!(typeof y === 'number' && Number.isFinite(y))) continue;

          // If the point has an X value, ensure it is within the currently selected X range.
          if (p && typeof p === 'object' && 'x' in p) {
            const xMs = new Date((p as any).x).getTime();
            if (!Number.isFinite(xMs)) continue;
            if (xMs < xMinVisible || xMs > xMaxVisible) continue;
          }

          return y;
        }
        return null;
      };
      
      // Try to get chart from ref first, then from global instance
      const chart = chartRef.current || globalChartInstance;
      
      if (!chart) {
        return;
      }

      if (!chart.scales || !chart.scales.y) {
        return;
      }

      // Also update fixedChartDimensions if not set yet
      if (chart.chartArea && fixedChartDimensions.current.height === 0) {
        fixedChartDimensions.current = {
          top: chart.chartArea.top,
          bottom: chart.chartArea.bottom,
          height: chart.chartArea.bottom - chart.chartArea.top
        };
      }

      const normalizedValue = normalizedPEOverlayValue;
      const normalizedAxis = chart.scales.y2;
      const normalizedPixel = normalizedAxis && typeof normalizedValue === 'number' && Number.isFinite(normalizedValue) && normalizedValue > 0
        ? normalizedAxis.getPixelForValue(normalizedValue)
        : null;
      setNormalizedPEPixelY(typeof normalizedPixel === 'number' && Number.isFinite(normalizedPixel) ? normalizedPixel : null);
      const normalizedPocfValue = normalizedPOCFOverlayValue;
      const normalizedPocfPixel = normalizedAxis && typeof normalizedPocfValue === 'number' && Number.isFinite(normalizedPocfValue) && normalizedPocfValue > 0
        ? normalizedAxis.getPixelForValue(normalizedPocfValue)
        : null;
      setNormalizedPOCFPixelY(typeof normalizedPocfPixel === 'number' && Number.isFinite(normalizedPocfPixel) ? normalizedPocfPixel : null);

      const positions: any = { price: null, revenue: null, grossProfit: null, operatingIncome: null, pretaxIncome: null, netIncome: null, pe: null, pOcf: null, pFcf: null, eps: null, cash: null, totalAssets: null, shortTermDebt: null, longTermDebt: null, totalLiabilities: null, shareholdersEquity: null, operatingCashFlow: null, freeCashFlow: null, dividends: null, buybacks: null, grossProfitMargin: null, operatingMargin: null, netMargin: null, operatingCFMargin: null, freeCFMargin: null, ocfToNetIncome: null, fcfToNetIncome: null, roic: null, flatEPSTTM: null, flatOCFTTM: null, fairPrice: null, fairPriceFlatEPSTTM: null, entryPrice: null, exitPrice: null };

      // Price
      const priceElement = elements.find(e => e.id === 'price-line');
      if (priceElement?.visible && priceElement.data.length > 0 && chart.scales.y) {
        // Get the actual rendered dataset from the chart.
        // Using only yAxisID can accidentally grab a different series that also uses 'y'.
        const dataset =
          chart.data.datasets.find((ds: any) => ds?.yAxisID === 'y' && ds?.label === 'Price') ||
          chart.data.datasets.find((ds: any) => ds?.elementId === 'price-line') ||
          chart.data.datasets.find((ds: any) => ds?.yAxisID === 'y');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y.getPixelForValue(lastValue);
            positions.price = pixelY;
          }
        }
      }

      // Revenue
      const revenueElement = elements.find(e => e.id === 'revenue');
      if (revenueElement?.visible && revenueElement.data.length > 0 && chart.scales.y1) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y1' && ds.label === 'Revenue');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y1.getPixelForValue(lastValue);
            positions.revenue = pixelY;
          }
        }
      }

      // Gross Profit
      const grossProfitElement = elements.find(e => e.id === 'gross-profit');
      if (grossProfitElement?.visible && grossProfitElement.data.length > 0 && chart.scales.y1) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y1' && ds.label === 'Gross Profit');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y1.getPixelForValue(lastValue);
            positions.grossProfit = pixelY;
          }
        }
      }

      // Operating Income
      const operatingIncomeElement = elements.find(e => e.id === 'operating-income');
      if (operatingIncomeElement?.visible && operatingIncomeElement.data.length > 0 && chart.scales.y1) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y1' && ds.label === 'Operating Income');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y1.getPixelForValue(lastValue);
            positions.operatingIncome = pixelY;
          }
        }
      }

      // Pretax Income
      const pretaxIncomeElement = elements.find(e => e.id === 'pretax-income');
      if (pretaxIncomeElement?.visible && pretaxIncomeElement.data.length > 0 && chart.scales.y1) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y1' && ds.label === 'Pretax Income');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y1.getPixelForValue(lastValue);
            positions.pretaxIncome = pixelY;
          }
        }
      }

      // Net Income
      const netIncomeElement = elements.find(e => e.id === 'net-income');
      if (netIncomeElement?.visible && netIncomeElement.data.length > 0 && chart.scales.y1) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y1' && ds.label === 'Net Income');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            positions.netIncome = chart.scales.y1.getPixelForValue(lastValue);
          }
        }
      }

      // PE
      const peElement = elements.find(e => e.id === 'pe');
      if (peElement?.visible && peElement.data.length > 0 && chart.scales.y2) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'pe');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y2.getPixelForValue(lastValue);
            positions.pe = pixelY;
          }
        }
      }

      // P/OCF
      const pOcfElement = elements.find(e => e.id === 'p-ocf-ratio');
      if (pOcfElement?.visible && pOcfElement.data.length > 0 && chart.scales.y2) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'p-ocf-ratio');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y2.getPixelForValue(lastValue);
            positions.pOcf = pixelY;
          }
        }
      }

      // P/FCF
      const pFcfElement = elements.find(e => e.id === 'p-fcf-ratio');
      if (pFcfElement?.visible && pFcfElement.data.length > 0 && chart.scales.y2) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'p-fcf-ratio');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y2.getPixelForValue(lastValue);
            positions.pFcf = pixelY;
          }
        }
      }

      // EPS
      const epsElement = elements.find(e => e.id === 'eps-diluted');
      if (epsElement?.visible && epsElement.data.length > 0 && chart.scales.y3) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y3');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y3.getPixelForValue(lastValue);
            positions.eps = pixelY;
          }
        }
      }

      // Cash
      const cashElement = elements.find(e => e.id === 'cash-equivalents');
      if (cashElement?.visible && cashElement.data.length > 0 && chart.scales.y4) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y4');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y4.getPixelForValue(lastValue);
            positions.cash = pixelY;
          }
        }
      }

      // Total Assets
      const totalAssetsElement = elements.find(e => e.id === 'total-assets');
      if (totalAssetsElement?.visible && totalAssetsElement.data.length > 0 && chart.scales.y5) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y5');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y5.getPixelForValue(lastValue);
            positions.totalAssets = pixelY;
          }
        }
      }

      // Short-Term Debt
      const shortTermDebtElement = elements.find(e => e.id === 'short-term-debt');
      if (shortTermDebtElement?.visible && shortTermDebtElement.data.length > 0 && chart.scales.y6) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y6');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y6.getPixelForValue(lastValue);
            positions.shortTermDebt = pixelY;
          }
        }
      }

      // Long-Term Debt
      const longTermDebtElement = elements.find(e => e.id === 'long-term-debt');
      if (longTermDebtElement?.visible && longTermDebtElement.data.length > 0 && chart.scales.y7) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y7');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y7.getPixelForValue(lastValue);
            positions.longTermDebt = pixelY;
          }
        }
      }

      const totalLiabilitiesElement = elements.find(e => e.id === 'total-liabilities');
      if (totalLiabilitiesElement?.visible && totalLiabilitiesElement.data.length > 0 && chart.scales.y8) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y8');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y8.getPixelForValue(lastValue);
            positions.totalLiabilities = pixelY;
          }
        }
      }

      const shareholdersEquityElement = elements.find(e => e.id === 'shareholders-equity');
      if (shareholdersEquityElement?.visible && shareholdersEquityElement.data.length > 0 && chart.scales.y9) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y9');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y9.getPixelForValue(lastValue);
            positions.shareholdersEquity = pixelY;
          }
        }
      }

      const operatingCashFlowElement = elements.find(e => e.id === 'operating-cash-flow');
      if (operatingCashFlowElement?.visible && operatingCashFlowElement.data.length > 0 && chart.scales.y10) {
        const dataset = chart.data.datasets.find(
          (ds: any) =>
            ds.yAxisID === 'y10' && (ds.label === 'Operating Cash Flow' || ds.label === 'Operating Cash Flow Margin')
        );
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y10.getPixelForValue(lastValue);
            positions.operatingCashFlow = pixelY;
          }
        }
      }

      const freeCashFlowElement = elements.find(e => e.id === 'free-cash-flow');
      if (freeCashFlowElement?.visible && freeCashFlowElement.data.length > 0 && chart.scales.y10) {
        const dataset = chart.data.datasets.find(
          (ds: any) => ds.yAxisID === 'y10' && (ds.label === 'Free Cash Flow' || ds.label === 'Free Cash Flow Margin')
        );
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y10.getPixelForValue(lastValue);
            positions.freeCashFlow = pixelY;
          }
        }
      }

      const dividendsElement = elements.find(e => e.id === 'dividends');
      if (dividendsElement?.visible && dividendsElement.data.length > 0 && chart.scales.y12) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y12');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y12.getPixelForValue(lastValue);
            positions.dividends = pixelY;
          }
        }
      }

      const buybacksElement = elements.find(e => e.id === 'buybacks');
      if (buybacksElement?.visible && buybacksElement.data.length > 0 && chart.scales.y13) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y13');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y13.getPixelForValue(lastValue);
            positions.buybacks = pixelY;
          }
        }
      }

      const grossProfitMarginElement = elements.find(e => e.id === 'gross-profit-margin');
      if (grossProfitMarginElement?.visible && grossProfitMarginElement.data.length > 0 && chart.scales.y14) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y14' && ds.label === 'Gross Profit Margin');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y14.getPixelForValue(lastValue);
            positions.grossProfitMargin = pixelY;
          }
        }
      }

      const operatingMarginElement = elements.find(e => e.id === 'operating-margin');
      if (operatingMarginElement?.visible && operatingMarginElement.data.length > 0 && chart.scales.y14) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y14' && ds.label === 'Operating Margin');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y14.getPixelForValue(lastValue);
            positions.operatingMargin = pixelY;
          }
        }
      }

      const netMarginElement = elements.find(e => e.id === 'net-margin');
      if (netMarginElement?.visible && netMarginElement.data.length > 0 && chart.scales.y14) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y14' && ds.label === 'Net Margin');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y14.getPixelForValue(lastValue);
            positions.netMargin = pixelY;
          }
        }
      }

      const operatingCFMarginElement = elements.find(e => e.id === 'operating-cf-margin');
      if (operatingCFMarginElement?.visible && operatingCFMarginElement.data.length > 0 && chart.scales.y14) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y14' && ds.label === 'Operating Cash Flow Margin');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y14.getPixelForValue(lastValue);
            positions.operatingCFMargin = pixelY;
          }
        }
      }

      const freeCFMarginElement = elements.find(e => e.id === 'free-cf-margin');
      if (freeCFMarginElement?.visible && freeCFMarginElement.data.length > 0 && chart.scales.y14) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y14' && ds.label === 'Free Cash Flow Margin');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y14.getPixelForValue(lastValue);
            positions.freeCFMargin = pixelY;
          }
        }
      }

      // Y19 - OCF to Net Income
      const ocfToNetIncomeElement = elements.find(e => e.id === 'operating-cf-to-ni');
      if (ocfToNetIncomeElement?.visible && ocfToNetIncomeElement.data.length > 0 && chart.scales.y19) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y19');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y19.getPixelForValue(lastValue);
            positions.ocfToNetIncome = pixelY;
          }
        }
      }

      // Y20 - FCF to Net Income
      const fcfToNetIncomeElement = elements.find(e => e.id === 'free-cf-to-ni');
      if (fcfToNetIncomeElement?.visible && fcfToNetIncomeElement.data.length > 0 && chart.scales.y20) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y20');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y20.getPixelForValue(lastValue);
            positions.fcfToNetIncome = pixelY;
          }
        }
      }

      // Calculate last value position for Y21 (ROIC)
      const roicElement = elements.find(e => e.id === 'roic');
      if (roicElement?.visible && roicElement.data.length > 0 && chart.scales.y21) {
        const dataset = chart.data.datasets.find((ds: any) => ds.yAxisID === 'y21');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y21.getPixelForValue(lastValue);
            positions.roic = pixelY;
          }
        }
      }

      // Calculate last value position for Y22 (Flat EPS TTM)
      const flatEPSTTMElement = elements.find(e => e.id === 'flat-eps-ttm');
      if (flatEPSTTMElement?.visible && flatEPSTTMElement.data.length > 0 && chart.scales.y22) {
        const lastValue = getLastFiniteYInVisibleRange(flatEPSTTMElement.data as any[]);
        if (lastValue !== null) {
          const pixelY = chart.scales.y22.getPixelForValue(lastValue);
          positions.flatEPSTTM = pixelY;
        }
      }

      // Calculate last value position for Y15 (Flat OCF/Share TTM)
      const flatOCFTTMElement = elements.find(e => e.id === 'flat-ocf-ttm');
      if (flatOCFTTMElement?.visible && flatOCFTTMElement.data.length > 0 && chart.scales.y15) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'flat-ocf-ttm');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = chart.scales.y15.getPixelForValue(lastValue);
            positions.flatOCFTTM = pixelY;
          }
        }
      }

      const ocfPriceAxis = priceVisible ? chart.scales.y : chart.scales.y16;

      const fairPriceOcfElement = elements.find(e => e.id === 'fair-price-ocf');
      if (fairPriceOcfElement?.visible && fairPriceOcfElement.data.length > 0 && ocfPriceAxis) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'fair-price-ocf');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = ocfPriceAxis.getPixelForValue(lastValue);
            positions.fairPriceOcf = pixelY;
          }
        }
      }

      const entryPriceOcfElement = elements.find(e => e.id === 'entry-point-1-ocf');
      if (entryPriceOcfElement?.visible && entryPriceOcfElement.data.length > 0 && ocfPriceAxis) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'entry-point-1-ocf');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = ocfPriceAxis.getPixelForValue(lastValue);
            positions.entryPriceOcf = pixelY;
          }
        }
      }

      const exitPriceOcfElement = elements.find(e => e.id === 'entry-point-2-ocf');
      if (exitPriceOcfElement?.visible && exitPriceOcfElement.data.length > 0 && ocfPriceAxis) {
        const dataset = chart.data.datasets.find((ds: any) => ds.elementId === 'entry-point-2-ocf');
        if (dataset && dataset.data && dataset.data.length > 0) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = ocfPriceAxis.getPixelForValue(lastValue);
            positions.exitPriceOcf = pixelY;
          }
        }
      }

      // Calculate last value position for Y22 (Fair Price)
      const fairPriceElement = elements.find(e => e.id === 'fair-price');
      if (fairPriceElement?.visible && fairPriceElement.data.length > 0) {
        const dataset = chart.data.datasets.find((ds: any) => ds.label === 'Fair Price');
        const yAxisId = dataset && typeof (dataset as any).yAxisID === 'string' ? (dataset as any).yAxisID : null;
        const yAxis = yAxisId ? (chart.scales as any)?.[yAxisId] : null;
        if (dataset && dataset.data && dataset.data.length > 0 && yAxis) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = yAxis.getPixelForValue(lastValue);
            positions.fairPrice = pixelY;
          }
        }
      }

      // Calculate last value position for Y22 (Fair Price Flat EPS (ttm))
      const fairPriceFlatEPSTTMElement = elements.find(e => e.id === 'fair-price-flat-eps-ttm');
      if (fairPriceFlatEPSTTMElement?.visible && fairPriceFlatEPSTTMElement.data.length > 0) {
        const dataset = chart.data.datasets.find((ds: any) => ds.label === 'Fair Price Flat EPS (ttm)');
        const yAxisId = dataset && typeof (dataset as any).yAxisID === 'string' ? (dataset as any).yAxisID : null;
        const yAxis = yAxisId ? (chart.scales as any)?.[yAxisId] : null;
        if (dataset && dataset.data && dataset.data.length > 0 && yAxis) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = yAxis.getPixelForValue(lastValue);
            positions.fairPriceFlatEPSTTM = pixelY;
          }
        }
      }

      // Calculate last value position for Y23 (Entry Price)
      const entryPriceElement = elements.find(e => e.id === 'entry-point-1');
      if (entryPriceElement?.visible && entryPriceElement.data.length > 0) {
        const dataset = chart.data.datasets.find((ds: any) => ds.label === 'Entry Price');
        const yAxisId = dataset && typeof (dataset as any).yAxisID === 'string' ? (dataset as any).yAxisID : null;
        const yAxis = yAxisId ? (chart.scales as any)?.[yAxisId] : null;
        if (dataset && dataset.data && dataset.data.length > 0 && yAxis) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = yAxis.getPixelForValue(lastValue);
            positions.entryPrice = pixelY;
          }
        }
      }

      // Calculate last value position for Y23 (Exit Price)
      const exitPriceElement = elements.find(e => e.id === 'entry-point-2');
      if (exitPriceElement?.visible && exitPriceElement.data.length > 0) {
        const dataset = chart.data.datasets.find((ds: any) => ds.label === 'Exit Price');
        const yAxisId = dataset && typeof (dataset as any).yAxisID === 'string' ? (dataset as any).yAxisID : null;
        const yAxis = yAxisId ? (chart.scales as any)?.[yAxisId] : null;
        if (dataset && dataset.data && dataset.data.length > 0 && yAxis) {
          const lastValue = getLastFiniteYInVisibleRange(dataset.data);
          if (lastValue !== null) {
            const pixelY = yAxis.getPixelForValue(lastValue);
            positions.exitPrice = pixelY;
          }
        }
      }

      setLastValuePositions(positions);
    };

    // Recompute quickly when elements (including slider adjustments) change.
    // Chart.js may update scales on the next frame, so retry a few times via rAF.
    let cancelled = false;
    let raf1: number | null = null;
    let raf2: number | null = null;
    let raf3: number | null = null;

    // Try immediately first.
    calculateLastValuePositions();

    raf1 = requestAnimationFrame(() => {
      if (cancelled) return;
      calculateLastValuePositions();
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        calculateLastValuePositions();
        raf3 = requestAnimationFrame(() => {
          if (cancelled) return;
          calculateLastValuePositions();
        });
      });
    });

    return () => {
      cancelled = true;
      if (raf1 !== null) cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (raf3 !== null) cancelAnimationFrame(raf3);
    };
  }, [elements, normalizedPEOverlayValue, normalizedPOCFOverlayValue]);

  const datasets = elements
    .filter(element => element.visible)
    .map(element => {
      // Apply percentage adjustment to data
      let adjustedData = element.data.map((point: any) => {
        const adjustmentFactor = (() => {
          // Entry/Exit series are already adjusted in the screen layer.
          if (
            element.id === 'entry-point-1' ||
            element.id === 'entry-point-2' ||
            element.id === 'entry-point-1-ocf' ||
            element.id === 'entry-point-2-ocf'
          ) {
            return 1;
          }
          return 1 + (element.adjustment / 100);
        })();
        // Handle both {x, y} format and simple number format
        if (typeof point === 'object' && point !== null && 'y' in point) {
          const y = (point as any).y;
          if (y === null || y === undefined) {
            return { ...point, y: null };
          }
          if (typeof y !== 'number' || !Number.isFinite(y)) {
            return { ...point, y: null };
          }
          return { ...point, y: y * adjustmentFactor };
        }

        if (point === null || point === undefined) {
          return null;
        }
        const y = typeof point === 'number' ? point : Number(point);
        return Number.isFinite(y) ? y * adjustmentFactor : null;
      });

      // Ensure the time-scale X axis can extend beyond the last datapoint.
      // When Price is hidden and only quarterly/annual financial points are visible,
      // Chart.js (with bounds derived from data) can appear to end at the last
      // financial date. We append a trailing null point at xMax to keep the
      // right side through the selected end date (typically today).
      if (xMaxDate) {
        const maxTs = xMaxDate.getTime();
        let lastTs: number | null = null;
        for (let i = adjustedData.length - 1; i >= 0; i--) {
          const p = adjustedData[i] as any;
          if (p && typeof p === 'object' && 'x' in p) {
            const ts = new Date(p.x).getTime();
            if (Number.isFinite(ts)) {
              lastTs = ts;
              break;
            }
          }
        }

        const canAppend = adjustedData.some((p: any) => p && typeof p === 'object' && 'x' in p);
        if (canAppend && (lastTs === null || lastTs < maxTs)) {
          adjustedData = [...adjustedData, { x: xMaxDate, y: null }];
        }
      }

      // In the special "Flat EPS axis == Price axis" mode, scale EPS values into
      // Price units so the series doesn't collapse to the bottom.


      // No need for revenue sparsing - financial data is already sparse by nature
      
      // Determine which Y axis to use
      let yAxisID: string;
      if (
        element.id === 'flat-eps-ttm'
      ) {
        // Flat EPS (ttm) uses its own axis, but we sync it 1:1 to the EPS axis.
        yAxisID = 'y22';
      } else if (element.id === 'flat-ocf-ttm') {
        yAxisID = 'y15'; // Flat OCF/Share (ttm) uses its own axis
      } else if (
        element.id === 'fair-price-ocf' ||
        element.id === 'entry-point-1-ocf' ||
        element.id === 'entry-point-2-ocf'
      ) {
        const isPriceElementVisible = elements.some(e => e.id === 'price-line' && e.visible);
        yAxisID = isPriceElementVisible ? 'y' : 'y16'; // Analysis OCF fair/entry/exit price axis
      } else if (
        element.id === 'fair-price' ||
        element.id === 'fair-price-flat-eps-ttm' ||
        element.id === 'entry-point-1' ||
        element.id === 'entry-point-2'
      ) {
        // Fair Price uses its own axis; when Price is visible we sync its min/max to Price.
        yAxisID = 'y23';
      } else if (element.id === 'roic') {
        yAxisID = 'y21'; // ROIC uses its own axis
      } else if (element.id === 'free-cf-to-ni') {
        yAxisID = 'y20'; // Free CF to Net Income uses its own axis
      } else if (element.id === 'operating-cf-to-ni') {
        yAxisID = 'y19'; // Operating CF to Net Income uses its own axis
      } else if (
        element.id === 'gross-profit-margin' ||
        element.id === 'operating-margin' ||
        element.id === 'net-margin' ||
        element.id === 'operating-cf-margin' ||
        element.id === 'free-cf-margin'
      ) {
        // All margin series share one percent axis
        yAxisID = 'y14';
      } else if (element.id === 'buybacks') {
        yAxisID = 'y13'; // Buybacks uses its own axis
      } else if (element.id === 'dividends') {
        yAxisID = 'y12'; // Dividends uses its own axis
      } else if (element.id === 'free-cash-flow') {
        yAxisID = 'y10'; // Cash Flow (Operating + Free) share the same axis
      } else if (element.id === 'operating-cash-flow') {
        yAxisID = 'y10'; // Cash Flow (Operating + Free) share the same axis
      } else if (element.id === 'shareholders-equity') {
        yAxisID = 'y9'; // Shareholders Equity uses its own axis
      } else if (element.id === 'total-liabilities') {
        yAxisID = 'y8'; // Total Liabilities uses its own axis
      } else if (element.id === 'long-term-debt') {
        yAxisID = 'y7'; // Long-Term Debt uses its own axis
      } else if (element.id === 'short-term-debt') {
        yAxisID = 'y6'; // Short-Term Debt uses its own axis
      } else if (element.id === 'total-assets') {
        yAxisID = 'y5'; // Total Assets uses its own axis
      } else if (element.id === 'cash-equivalents') {
        yAxisID = 'y4'; // Cash uses its own axis
      } else if (element.id === 'eps-diluted') {
        yAxisID = 'y3'; // EPS uses its own axis
      } else if (element.id === 'p-ocf-ratio' || element.id === 'p-fcf-ratio') {
        yAxisID = 'y2'; // Valuation ratios share the PE axis
      } else if (element.id === 'pe') {
        yAxisID = 'y2'; // PE uses its own axis
      } else if (element.id === 'price-line') {
        // Price must always use the main price axis.
        // In short ranges (e.g., 5D) the dataset length is small, and the generic
        // heuristic below would incorrectly route it to y1, leaving the price axis
        // (y) with no data and default ticks like 0–1.
        yAxisID = 'y';
      } else {
        // Financial data (Revenue, etc.) uses right axis y1
        const isFinancialData = element.useTimeScale || element.data.length < 100;
        yAxisID = isFinancialData ? 'y1' : 'y';
      }

      const baseConfig = {
        elementId: element.id,
        label: element.name,
        data: adjustedData,
        borderColor: element.color,
        backgroundColor: element.color + '20',
        // Give markers/lines a few px beyond chartArea to avoid edge clipping.
        // For synthetic step/segment series, this extra clip makes the last segment appear
        // to extend past the selected X max (e.g., beyond 2026). Keep them strictly clipped.
        clip: (
          element.id === 'flat-eps-ttm' ||
          element.id === 'flat-ocf-ttm' ||
          element.id === 'fair-price-ocf' ||
          element.id === 'entry-point-1-ocf' ||
          element.id === 'entry-point-2-ocf' ||
          element.id === 'fair-price' ||
          element.id === 'fair-price-flat-eps-ttm' ||
          element.id === 'entry-point-1' ||
          element.id === 'entry-point-2'
        ) ? 0 : 12,
        tension: (
          element.id === 'flat-eps-ttm' ||
          element.id === 'flat-ocf-ttm' ||
          element.id === 'fair-price-ocf' ||
          element.id === 'entry-point-1-ocf' ||
          element.id === 'entry-point-2-ocf' ||
          element.id === 'fair-price'
        ) ? 0 : 0.1,
        yAxisID: yAxisID, // Assign to appropriate Y axis
        pointRadius: (element as any).showPoints === false ? 0 : 3,
        pointHoverRadius: (element as any).showPoints === false ? 0 : 5,
        ...(element.stepped ? { 
          stepped: 'before' as const,
          segment: {
            borderColor: (ctx: any) => {
              // Hide ONLY the vertical step connectors.
              // In Chart.js stepped mode, vertical connectors are drawn as segments where X doesn't change.
              const p0 = ctx.p0;
              const p1 = ctx.p1;
              if (p0?.parsed && p1?.parsed && p0.parsed.x === p1.parsed.x) {
                return 'transparent';
              }
              return element.color;
            }
          }
        } : {}),
      };

      switch (element.type) {
        case 'entry-point':
          return {
            ...baseConfig,
            pointRadius: 6,
            pointHoverRadius: 8,
            borderWidth: 2, // Changed from 3 to 1
            pointBackgroundColor: element.color,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            showLine: false,
          };
        case 'support':
        case 'resistance':
          return {
            ...baseConfig,
            borderDash: [5, 5],
            borderWidth: 2, // Changed from 2 to 1
            pointRadius: 0,
            pointHoverRadius: 6,
          };
        default:
          // Default line behavior - show points only for sparse data (like Revenue)
          // For dense data (like Price with thousands of points), keep it as a line
          const isSparsData = element.useTimeScale || element.data.length < 100;
          
          const showPoints = (element as any).showPoints !== false;
          const isSteppedLine = element.stepped === true;
          const isEntryExitPrice =
            element.id === 'entry-point-1' ||
            element.id === 'entry-point-2' ||
            element.id === 'entry-point-1-ocf' ||
            element.id === 'entry-point-2-ocf';
          
          const defaultDataset = {
            ...baseConfig,
            borderWidth: isDateRangeLocked && isEntryExitPrice ? 3 : 2,
            spanGaps: false, // Don't connect null gaps
            pointRadius: !showPoints ? 0 : (isSteppedLine ? 4 : (isSparsData ? 4 : 0)),
            pointHoverRadius: !showPoints ? 0 : (isSteppedLine ? 6 : 6),
            pointBorderWidth: 2,
            pointBackgroundColor: element.color,
            pointBorderColor: element.id === 'pe' ? element.color : (isDarkMode ? '#1f2937' : '#ffffff'),
            showLine: true, // Always show connecting line
          };

          return defaultDataset;
      }
    });

  const xDataRange = React.useMemo<{ min: number; max: number } | null>(() => {
    let minTs: number | null = null;
    let maxTs: number | null = null;

    const pushTs = (ts: number) => {
      if (!Number.isFinite(ts)) return;
      minTs = minTs === null ? ts : Math.min(minTs, ts);
      maxTs = maxTs === null ? ts : Math.max(maxTs, ts);
    };

    for (const ds of datasets as any[]) {
      const points = (ds as any)?.data;
      if (!Array.isArray(points)) continue;
      for (const pt of points) {
        if (pt && typeof pt === 'object' && 'x' in pt) {
          pushTs(new Date((pt as any).x).getTime());
        }
      }
    }

    if (minTs === null || maxTs === null) {
      for (const lbl of (labels ?? []) as any[]) {
        pushTs(new Date(lbl).getTime());
      }
    }

    if (minTs === null || maxTs === null) return null;
    return { min: minTs, max: maxTs };
  }, [datasets, labels]);

  const effectiveXBounds = React.useMemo<{ min: number; max: number } | null>(() => {
    if (!xDataRange) return null;
    const selectedMin = xMin ? parseTimeBound(xMin, false) : xDataRange.min;
    const selectedMax = xMax ? parseTimeBound(xMax, true) : xDataRange.max;

    const isPriceVisible = elements?.some(e => e.id === 'price-line' && e.visible);

    // IMPORTANT:
    // - When Price is visible, clamp X to the last visible datapoint so ranges like 5D
    //   don't show empty future time (intraday: don't show the rest of the trading day).
    // - When Price is hidden (financial-only), do NOT clamp to xDataRange.max because the
    //   last datapoint is often a past quarter; we still want the chart to render through
    //   the selected end date (typically today) so the X axis doesn't shrink.
    const min = isPriceVisible ? Math.max(selectedMin, xDataRange.min) : selectedMin;
    const max = isPriceVisible ? Math.min(selectedMax, xDataRange.max) : selectedMax;

    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
      return { min: xDataRange.min, max: xDataRange.max };
    }

    return { min, max };
  }, [xDataRange, xMin, xMax, elements]);


  const xTimeConfig = React.useMemo(() => {
    if (!xMin || !xMax) {
      return {
        unit: 'year' as const,
        displayFormats: {
          year: 'yyyy',
          month: 'MMM yyyy',
          week: 'MMM d',
          day: 'MMM d, yyyy',
        },
        tooltipFormat: 'MMM d, yyyy',
      };
    }

    const start = parseTimeBound(xMin, false);
    const end = parseTimeBound(xMax, true);
    const diffDays = Number.isFinite(start) && Number.isFinite(end)
      ? Math.max(0, (end - start) / 86400000)
      : 365;

    if (diffDays <= 10) {
      return {
        unit: 'day' as const,
        displayFormats: {
          day: 'MMM d',
          month: 'MMM yyyy',
          year: 'yyyy',
        },
        tooltipFormat: 'MMM d, yyyy',
      };
    }

    if (diffDays <= 90) {
      return {
        unit: 'week' as const,
        displayFormats: {
          week: 'MMM d',
          month: 'MMM yyyy',
          year: 'yyyy',
        },
        tooltipFormat: 'MMM d, yyyy',
      };
    }

    if (diffDays <= 800) {
      return {
        unit: 'month' as const,
        displayFormats: {
          month: 'MMM yyyy',
          year: 'yyyy',
        },
        tooltipFormat: 'MMM d, yyyy',
      };
    }

    return {
      unit: 'year' as const,
      displayFormats: {
        year: 'yyyy',
        month: 'MMM yyyy',
        day: 'MMM d, yyyy',
      },
      tooltipFormat: 'MMM d, yyyy',
    };
  }, [xMin, xMax]);

  const priceVisible = elements.some(e => e.id === 'price-line' && e.visible);
  const incomeVisible = elements.some(e => (
    e.id === 'revenue' ||
    e.id === 'gross-profit' ||
    e.id === 'operating-income' ||
    e.id === 'pretax-income' ||
    e.id === 'net-income'
  ) && e.visible);
  const peVisible = elements.some(e => e.id === 'pe' && e.visible);
  const pOcfVisible = elements.some(e => e.id === 'p-ocf-ratio' && e.visible);
  const pFcfVisible = elements.some(e => e.id === 'p-fcf-ratio' && e.visible);
  const valuationRatioVisible = peVisible || pOcfVisible || pFcfVisible;
  const epsVisible = elements.some(e => e.id === 'eps-diluted' && e.visible);
  const cashVisible = elements.some(e => e.id === 'cash-equivalents' && e.visible);
  const totalAssetsVisible = elements.some(e => e.id === 'total-assets' && e.visible);
  const shortTermDebtVisible = elements.some(e => e.id === 'short-term-debt' && e.visible);
  const longTermDebtVisible = elements.some(e => e.id === 'long-term-debt' && e.visible);
  const totalLiabilitiesVisible = elements.some(e => e.id === 'total-liabilities' && e.visible);
  const equityVisible = elements.some(e => e.id === 'shareholders-equity' && e.visible);
  const cashFlowVisible = elements.some(e => (e.id === 'operating-cash-flow' || e.id === 'free-cash-flow') && e.visible);
  const dividendsVisible = elements.some(e => e.id === 'dividends' && e.visible);
  const buybacksVisible = elements.some(e => e.id === 'buybacks' && e.visible);
  const grossProfitMarginVisible = elements.some(e => e.id === 'gross-profit-margin' && e.visible);
  const operatingMarginVisible = elements.some(e => e.id === 'operating-margin' && e.visible);
  const netMarginVisible = elements.some(e => e.id === 'net-margin' && e.visible);
  const operatingCFMarginVisible = elements.some(e => e.id === 'operating-cf-margin' && e.visible);
  const freeCFMarginVisible = elements.some(e => e.id === 'free-cf-margin' && e.visible);
  const ocfToNetIncomeVisible = elements.some(e => e.id === 'operating-cf-to-ni' && e.visible);
  const fcfToNetIncomeVisible = elements.some(e => e.id === 'free-cf-to-ni' && e.visible);
  const roicVisible = elements.some(e => e.id === 'roic' && e.visible);
  const flatEPSTTMVisible = elements.some(e => e.id === 'flat-eps-ttm' && e.visible);
  const flatOCFTTMVisible = elements.some(e => e.id === 'flat-ocf-ttm' && e.visible);
  const ocfPriceVisible = elements.some(e => (
    e.id === 'fair-price-ocf' ||
    e.id === 'entry-point-1-ocf' ||
    e.id === 'entry-point-2-ocf'
  ) && e.visible);
  const fairPriceVisible = elements.some(
    e => (e.id === 'fair-price' || e.id === 'fair-price-flat-eps-ttm' || e.id === 'entry-point-1' || e.id === 'entry-point-2') && e.visible
  );

  // EPS + Flat EPS axes must be strictly identical (separate axes, same numbers at same pixels).
  // We make this deterministic by explicitly setting the same min/max on BOTH y3 and y22.
  // IMPORTANT: do NOT force min=0; if the data contains negatives (e.g., quarterly), we must show them.
  const epsSharedAxisRange = React.useMemo<{ min: number; max: number } | null>(() => {
    const epsOn = elements.some(e => e.id === 'eps-diluted' && e.visible);
    const flatOn = elements.some(e => e.id === 'flat-eps-ttm' && e.visible);
    if (!epsOn && !flatOn) return null;

    const getFiniteMinMaxY = (data: any[]): { min: number; max: number } | null => {
      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;
      for (const p of data ?? []) {
        const yRaw = (p && typeof p === 'object' && 'y' in p) ? (p as any).y : p;
        const y = typeof yRaw === 'number' ? yRaw : (typeof yRaw === 'string' ? parseFloat(yRaw) : NaN);
        if (!Number.isFinite(y)) continue;
        if (y < min) min = y;
        if (y > max) max = y;
      }
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
      return { min, max };
    };

    const epsEl = elements.find(e => e.id === 'eps-diluted' && e.visible);
    const flatEl = elements.find(e => e.id === 'flat-eps-ttm' && e.visible);
    const epsMM = epsEl ? getFiniteMinMaxY(epsEl.data as any[]) : null;
    const flatMM = flatEl ? getFiniteMinMaxY(flatEl.data as any[]) : null;

    const minV = Math.min(epsMM?.min ?? 0, flatMM?.min ?? 0, 0);
    const maxV = Math.max(epsMM?.max ?? 0, flatMM?.max ?? 0, 0);
    const step = 2;
    const outMin = minV < 0 ? Math.floor(minV / step) * step : 0;
    let outMax = Math.ceil(Math.max(0, maxV) / step) * step;
    if (!Number.isFinite(outMax) || outMax <= outMin) outMax = outMin + step;

    return { min: outMin, max: outMax };
  }, [elements]);

  // When multiple chart elements are shown together, align all Y-axes so that the 0-line sits on the exact same horizontal gridline.
  // This avoids confusing baselines when comparing Price with financial statements/ratios.
  const alignZeroAcrossAxes = React.useMemo(() => elements.filter((e) => e.visible).length > 1, [elements]);

  const forcedPriceRange = React.useMemo<{ min: number; max: number } | null>(() => {
    // Used whenever a secondary axis must exactly match the Price axis (1:1).
    // - Price + Fair/Entry/Exit overlays: include those series when computing the range.
    if (!priceVisible || !fairPriceVisible) return null;

    const candidateIds = new Set<string>(['price-line']);
    if (fairPriceVisible) {
      candidateIds.add('fair-price');
      candidateIds.add('fair-price-flat-eps-ttm');
      candidateIds.add('entry-point-1');
      candidateIds.add('entry-point-2');
    }
    const candidates = elements.filter((e) => e.visible && candidateIds.has(e.id));
    if (candidates.length === 0) return null;

    const xMinVisible = typeof effectiveXBounds?.min === 'number' ? effectiveXBounds.min : Number.NEGATIVE_INFINITY;
    const xMaxVisible = typeof effectiveXBounds?.max === 'number' ? effectiveXBounds.max : Number.POSITIVE_INFINITY;

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (const series of candidates) {
      const adjustmentFactor = (() => {
        // Match the exact adjustment logic used when building chart datasets.
        // Entry/Exit sliders are decimals (e.g., 0.95 => +95% / -95%).
        if (series.id === 'entry-point-2') return 1 + (series.adjustment ?? 0);
        if (series.id === 'entry-point-1') return Math.max(0, 1 - (series.adjustment ?? 0));
        return 1 + ((series.adjustment ?? 0) / 100);
      })();

      const points = series.data;
      if (!Array.isArray(points) || points.length === 0) continue;

      for (const p of points as any[]) {
        if (!p || typeof p !== 'object') continue;
        const xRaw = (p as any).x;
        const yRaw = (p as any).y;
        const yBase = typeof yRaw === 'number' ? yRaw : (typeof yRaw === 'string' ? parseFloat(yRaw) : NaN);
        const y = yBase * adjustmentFactor;
        if (!Number.isFinite(y)) continue;

        const xMs = new Date(xRaw).getTime();
        if (!Number.isFinite(xMs)) continue;
        if (xMs < xMinVisible || xMs > xMaxVisible) continue;

        if (y < min) min = y;
        if (y > max) max = y;
      }
    }

    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

    const range = max - min;
    let pad = range > 0 ? range * 0.1 : Math.max(1, Math.abs(min || max) * 0.1);
    const cap = Math.max(1, Math.abs(max) * 0.05);
    pad = Math.min(pad, cap);

    const paddedMin = min - pad;
    const paddedMax = max + pad;

    const niceStep = (rawStep: number) => {
      const safe = Math.max(1e-12, Math.abs(rawStep));
      const exponent = Math.floor(Math.log10(safe));
      const base = Math.pow(10, exponent);
      const fraction = safe / base;

      let niceFraction = 1;
      if (fraction <= 1) niceFraction = 1;
      else if (fraction <= 2) niceFraction = 2;
      else if (fraction <= 2.5) niceFraction = 2.5;
      else if (fraction <= 5) niceFraction = 5;
      else niceFraction = 10;

      return niceFraction * base;
    };

    const targetTicks = 12;
    const paddedRange = Math.max(0, paddedMax - paddedMin);
    const rawStep = paddedRange > 0
      ? paddedRange / Math.max(1, (targetTicks - 1))
      : Math.max(1, Math.abs(paddedMax || paddedMin) * 0.1);
    const step = niceStep(rawStep);

    const maxDown = Math.floor(paddedMax / step) * step;
    const maxUp = Math.ceil(paddedMax / step) * step;
    let outMax = maxDown >= max ? maxDown : maxUp;

    const minDown = Math.floor(paddedMin / step) * step;
    const minUp = Math.ceil(paddedMin / step) * step;
    let outMin = minUp <= min ? minUp : minDown;

    // Prices can't be negative; clamp only if needed.
    outMin = Math.max(0, outMin);
    if (!Number.isFinite(outMin) || !Number.isFinite(outMax)) return null;
    if (outMax <= outMin) outMax = outMin + step;

    return { min: outMin, max: outMax };
  }, [elements, priceVisible, fairPriceVisible, effectiveXBounds]);

  // Exactly one non-price axis should own chart-area horizontal gridlines when Price is hidden.
  const gridOwnerAxis:
    | 'y1' | 'y2' | 'y3' | 'y4'
    | 'y5' | 'y6' | 'y7' | 'y8' | 'y9' | 'y10'
    | 'y12' | 'y13'
    | 'y14' | 'y15' | 'y16' | 'y17' | 'y18' | 'y19' | 'y20' | 'y21' | 'y22' | 'y23'
    | null = !priceVisible
    ? (incomeVisible ? 'y1'
      : valuationRatioVisible ? 'y2'
      : epsVisible ? 'y3'
      : cashVisible ? 'y4'
      : totalAssetsVisible ? 'y5'
      : shortTermDebtVisible ? 'y6'
      : longTermDebtVisible ? 'y7'
      : totalLiabilitiesVisible ? 'y8'
      : equityVisible ? 'y9'
      : cashFlowVisible ? 'y10'
      : dividendsVisible ? 'y12'
      : buybacksVisible ? 'y13'
      : (grossProfitMarginVisible || operatingMarginVisible || netMarginVisible || operatingCFMarginVisible || freeCFMarginVisible) ? 'y14'
      : ocfToNetIncomeVisible ? 'y19'
      : fcfToNetIncomeVisible ? 'y20'
      : roicVisible ? 'y21'
      : flatEPSTTMVisible ? 'y22'
      : flatOCFTTMVisible ? 'y15'
      : ocfPriceVisible ? 'y16'
      : fairPriceVisible ? 'y23'
      : null)
    : null;

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 10,  // Reduced padding to minimize white space
        top: 10,
        bottom: 20,
      },
    },
    animation: false,
    plugins: ({
      crosshair: {
        enabled: true,
      },
      legend: {
        display: false,
        position: 'top' as const,
        labels: {
          color: textColor,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
        text: 'Advanced Trading Chart',
        color: textColor,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        enabled: false, // Disable the default tooltip - we use custom crosshair tooltips
      },
    } as any),
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: xTimeConfig.unit,
          displayFormats: xTimeConfig.displayFormats as any,
          tooltipFormat: xTimeConfig.tooltipFormat,
        },
        position: 'bottom',
        display: true,
        offset: false,
        bounds: 'data',
        border: {
          display: true,
          color: gridColor,
        },
        grid: {
          color: gridColor,
          drawOnChartArea: true,
          drawTicks: false,
          tickLength: 0,
        },
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
          maxRotation: 0,
          minRotation: 0,
          // Keep the first/last tick labels inside the chart area so boundary years (e.g., 2011)
          // don't get clipped on the left edge.
          align: 'inner',
          autoSkip: false,
        },
        ...(effectiveXBounds ? {
          min: effectiveXBounds.min,
          max: effectiveXBounds.max,
        } : {}),
      },
      y: {
        type: 'linear',
        display: true, // Always display to keep grid lines
        position: 'right',
        offset: !alignZeroAcrossAxes,
        bounds: 'data',
        grace: 0,
        ...(alignZeroAcrossAxes ? { beginAtZero: true } : {}),
        ...(forcedPriceRange ? { min: forcedPriceRange.min, max: forcedPriceRange.max } : {}),
        grid: {
          color: gridColor,
          // Only draw chart-area gridlines from the Price axis when Price is visible.
          // Otherwise, we let the selected financial axis (e.g., Revenue) draw the grid.
          drawOnChartArea: priceVisible,
          drawTicks: false,
          tickLength: 0,
        },
        ticks: {
          display: priceVisible, // Only show ticks if Price is visible
          color: textColor,
          font: {
            size: 10,
          },
          padding: 20,
          autoSkip: false,
          maxTicksLimit: 12,
          // If Entry/Exit force a max like 720 while stepSize is 50, includeBounds
          // causes an extra bound tick (720) very close to 700. In that case, let
          // ticks stay on the step grid.
          includeBounds: !forcedPriceRange,
          callback: function(value) {
            const num = (typeof value === 'number') ? value : parseFloat(String(value));
            if (Number.isFinite(num)) return formatAxisTickNumber(num);
            return String(value);
          },
        },
        border: axisBorder,
      },
      y1: {
        type: 'linear',
        display: elements.some(e => (e.id === 'revenue' || e.id === 'gross-profit' || e.id === 'operating-income' || e.id === 'pretax-income' || e.id === 'net-income') && e.visible), // Only show if Revenue, Gross Profit, Operating Income, Pretax Income, or Net Income is visible
        position: 'right',
        offset: !alignZeroAcrossAxes,
        ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
        grid: {
          // When Price is not shown, Revenue-family axis becomes the primary grid.
          drawOnChartArea: gridOwnerAxis === 'y1',
          color: gridColor,
          drawTicks: false,
          tickLength: 0,
        },
        ticks: {
          color: textColor,
          font: {
            size: 10,
          },
          padding: 15,
          maxTicksLimit: 6,
          callback: function(value, _index, ticks) {
            const num = typeof value === 'number' ? value : parseFloat(String(value));
            if (!Number.isFinite(num)) return String(value);

            const hasBillionsTick = Array.isArray(ticks)
              && ticks.some((tick) => {
                const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
              });

            if (num === 0 && hasBillionsTick) return '0.00B';

            // Format with ceiling rounding
            return formatRevenueWithUnits(num);
          },
        },
        border: axisBorder,
      },
      y2: {
        type: 'linear',
        display: valuationRatioVisible, // Show for PE / P-OCF / P-FCF
        position: 'right',
        offset: !alignZeroAcrossAxes,
        bounds: 'data',
        grace: 0,
        grid: {
          // If Price and Income are hidden, PE becomes the primary grid.
          drawOnChartArea: gridOwnerAxis === 'y2',
          color: gridColor,
          drawTicks: false,
          tickLength: 0,
        },
        ticks: {
          color: textColor,
          font: {
            size: 10,
          },
          padding: 15,
          maxTicksLimit: 6,
          callback: function(value) {
            if (typeof value === 'number') {
              // Format as whole number without decimals
              return Math.round(value).toString();
            }
            return value;
          },
        },
        border: axisBorder,
      },
      y3: {
        type: 'linear',
        display: elements.some(e => e.id === 'eps-diluted' && e.visible), // Only show if EPS is visible
        position: 'right',
        offset: !alignZeroAcrossAxes,
        // EPS axis must start at 0.00 and use 2.00 steps (so Flat EPS can duplicate it 1:1).
        beginAtZero: true,
        ...(epsSharedAxisRange ? { min: epsSharedAxisRange.min, max: epsSharedAxisRange.max } : { min: 0 }),
        grace: 0,
        grid: {
          // If Price/Income/PE are hidden, EPS becomes the primary grid.
          drawOnChartArea: gridOwnerAxis === 'y3',
          color: gridColor,
          drawTicks: false,
          tickLength: 0,
        },
        ticks: {
          color: textColor,
          font: {
            size: 10,
          },
          padding: 15,
          autoSkip: false,
          stepSize: 2,
          includeBounds: true,
          maxTicksLimit: 12,
          callback: function(value) {
            const num = (typeof value === 'number') ? value : parseFloat(String(value));
            if (Number.isFinite(num)) return num.toFixed(2);
            return String(value);
          },
        },
        border: axisBorder,
      },
      // Y4 axis - Cash and Cash Equivalents (right side)
      ...(elements.some(e => e.id === 'cash-equivalents' && e.visible) ? {
        y4: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: false,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : { grace: 0 }),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y4',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y5 axis - Total Assets (right side)
      ...(elements.some(e => e.id === 'total-assets' && e.visible) ? {
        y5: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y5',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 20,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y6 axis - Short-Term Debt (right side)
      ...(elements.some(e => e.id === 'short-term-debt' && e.visible) ? {
        y6: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y6',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y7 axis - Long-Term Debt (right side)
      ...(elements.some(e => e.id === 'long-term-debt' && e.visible) ? {
        y7: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y7',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y8 axis - Total Liabilities (right side)
      ...(elements.some(e => e.id === 'total-liabilities' && e.visible) ? {
        y8: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y8',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y9 axis - Shareholders Equity (right side)
      ...(elements.some(e => e.id === 'shareholders-equity' && e.visible) ? {
        y9: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y9',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y10 axis - Cash Flow (Operating + Free) (right side)
      ...(elements.some(e => (e.id === 'operating-cash-flow' || e.id === 'free-cash-flow') && e.visible) ? {
        y10: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y10',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y12 axis - Dividends (right side)
      ...(elements.some(e => e.id === 'dividends' && e.visible) ? {
        y12: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y12',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y13 axis - Buybacks (right side)
      ...(elements.some(e => e.id === 'buybacks' && e.visible) ? {
        y13: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y13',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value, _index, ticks) {
              const num = typeof value === 'number' ? value : parseFloat(String(value));
              if (!Number.isFinite(num)) return String(value);

              const hasBillionsTick = Array.isArray(ticks)
                && ticks.some((tick) => {
                  const raw = typeof tick === 'number' ? tick : (tick as { value?: unknown })?.value;
                  const tickNum = typeof raw === 'number' ? raw : parseFloat(String(raw));
                  return Number.isFinite(tickNum) && Math.abs(tickNum) >= 1000;
                });

              if (num === 0 && hasBillionsTick) return '0.00B';

              return formatRevenueWithUnits(num);
            },
          },
          border: axisBorder,
        },
      } : {}),
      // Y14 axis - Margin % (Gross Profit / Operating / Net / OCF / FCF)
      ...(elements.some(e => (
        e.id === 'gross-profit-margin' ||
        e.id === 'operating-margin' ||
        e.id === 'net-margin' ||
        e.id === 'operating-cf-margin' ||
        e.id === 'free-cf-margin'
      ) && e.visible) ? {
        y14: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y14',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value) {
              if (typeof value === 'number') {
                return Math.round(value).toString() + '%';
              }
              return value;
            },
          },
          border: axisBorder,
        },
      } : {}),

      // Y15 axis - Flat OCF/Share (ttm)
      ...(flatOCFTTMVisible ? {
        y15: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y15',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value: any) {
              const num = (typeof value === 'number') ? value : parseFloat(String(value));
              if (Number.isFinite(num)) return num.toFixed(2);
              return String(value);
            },
          },
          border: axisBorder,
        },
      } : {}),

      // Y16 axis - Analysis OCF Fair/Entry/Exit Price
      ...(ocfPriceVisible ? {
        y16: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          bounds: 'data',
          grace: 0,
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y16',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            autoSkip: false,
            maxTicksLimit: 12,
            callback: function(value: any) {
              const num = (typeof value === 'number') ? value : parseFloat(String(value));
              if (Number.isFinite(num)) return formatAxisTickNumber(num);
              return String(value);
            },
          },
          border: axisBorder,
        },
      } : {}),

      // Y19 axis - OCF to Net Income (right side)
      ...(flatEPSTTMVisible ? {
        y22: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          bounds: 'data',
          // Flat EPS axis must always be exactly: 0.00, 2.00, 4.00, 6.00, ...
          beginAtZero: true,
          ...(epsSharedAxisRange ? { min: epsSharedAxisRange.min, max: epsSharedAxisRange.max } : { min: 0 }),
          grace: 0,
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y22',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            autoSkip: false,
            stepSize: 2,
            includeBounds: true,
            maxTicksLimit: 12,
            callback: function(value: any) {
              const num = (typeof value === 'number') ? value : parseFloat(String(value));
              if (Number.isFinite(num)) return num.toFixed(2);
              return String(value);
            },
          },
          border: axisBorder,
        },
      } : {}),
      ...(elements.some(e => e.id === 'operating-cf-to-ni' && e.visible) ? {
        y19: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y19',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value) {
              if (typeof value === 'number') {
                return Math.round(value).toString() + '%';
              }
              return value;
            },
          },
          border: axisBorder,
        },
      } : {}),

      // Y20 axis - Free CF to Net Income (right side)
      ...(elements.some(e => e.id === 'free-cf-to-ni' && e.visible) ? {
        y20: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y20',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value) {
              if (typeof value === 'number') {
                return Math.round(value).toString() + '%';
              }
              return value;
            },
          },
          border: axisBorder,
        },
      } : {}),
      ...(elements.some(e => e.id === 'roic' && e.visible) ? {
        y21: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          ...(alignZeroAcrossAxes ? { beginAtZero: true, grace: 0 } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y21',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            maxTicksLimit: 6,
            callback: function(value) {
              if (typeof value === 'number') {
                return Math.round(value).toString() + '%';
              }
              return value;
            },
          },
          border: axisBorder,
        },
      } : {}),
      ...(fairPriceVisible ? {
        y23: {
          type: 'linear' as const,
          position: 'right' as const,
          display: true,
          offset: !alignZeroAcrossAxes,
          bounds: 'data',
          grace: 0,
          ...(forcedPriceRange ? { min: forcedPriceRange.min, max: forcedPriceRange.max } : {}),
          grid: {
            drawOnChartArea: gridOwnerAxis === 'y23',
            color: gridColor,
            drawTicks: false,
            tickLength: 0,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
            },
            padding: 15,
            autoSkip: false,
            maxTicksLimit: 12,
            includeBounds: !forcedPriceRange,
            callback: function(value) {
              const num = (typeof value === 'number') ? value : parseFloat(String(value));
              if (Number.isFinite(num)) return formatAxisTickNumber(num);
              return String(value);
            },
          },
          border: axisBorder,
        },
      } : {}),
    },
  };

  const chartAreaTopBorderPlugin = {
    id: 'chartAreaTopBorder',
    afterDraw: (chart: any) => {
      const chartArea = chart?.chartArea;
      const ctx: CanvasRenderingContext2D | undefined = chart?.ctx;
      if (!chartArea || !ctx) return;

      const { left, top, right } = chartArea;

      ctx.save();
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(left, top);
      ctx.lineTo(right, top);
      ctx.stroke();
      ctx.restore();
    },
  };

  const normalizedPeOverlayPlugin = {
    id: 'normalizedPeOverlay',
    afterDraw: (chart: any) => {
      const overlayValue = normalizedPEOverlayValueRef.current;
      const pocfOverlayValue = normalizedPOCFOverlayValueRef.current;
      const hasPeOverlay = typeof overlayValue === 'number' && Number.isFinite(overlayValue) && overlayValue > 0;
      const hasPocfOverlay = typeof pocfOverlayValue === 'number' && Number.isFinite(pocfOverlayValue) && pocfOverlayValue > 0;
      if (!hasPeOverlay && !hasPocfOverlay) return;

      const peAxis = chart?.scales?.y2;
      const chartArea = chart?.chartArea;
      const ctx: CanvasRenderingContext2D | undefined = chart?.ctx;
      if (!peAxis || !chartArea || !ctx) return;

      ctx.save();
      if (hasPeOverlay) {
        const y = peAxis.getPixelForValue(overlayValue);
        if (Number.isFinite(y) && y >= chartArea.top && y <= chartArea.bottom) {
          ctx.setLineDash([8, 6]);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(chartArea.left, y);
          ctx.lineTo(chartArea.right, y);
          ctx.stroke();
        }
      }

      if (hasPocfOverlay) {
        const pocfColor = (() => {
          const datasets = Array.isArray(chart?.data?.datasets) ? chart.data.datasets : [];
          const pocfDataset = datasets.find((dataset: any) => dataset?.elementId === 'p-ocf-ratio');
          const borderColor = pocfDataset?.borderColor;
          return typeof borderColor === 'string' && borderColor.trim().length > 0
            ? borderColor
            : '#f472b6';
        })();
        const y = peAxis.getPixelForValue(pocfOverlayValue);
        if (Number.isFinite(y) && y >= chartArea.top && y <= chartArea.bottom) {
          ctx.setLineDash([8, 6]);
          ctx.strokeStyle = pocfColor;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(chartArea.left, y);
          ctx.lineTo(chartArea.right, y);
          ctx.stroke();
        }
      }

      ctx.restore();
    },
  };

  const syncFairPriceAxisToPriceAxisPlugin = {
    id: 'syncFairPriceAxisToPriceAxis',
    afterUpdate: (chart: any) => {
      // When Price + Fair Price are both visible, "connect" the axes by making
      // y23 a 1:1 copy of the main price axis (y). This keeps ticks/pixels aligned.
      // If Entry/Exit forces y23's range, don't override it.
      if (!chart) return;
      if (!priceVisible || !fairPriceVisible) return;
      if (forcedPriceRange) return;

      const priceAxis = chart?.scales?.y;
      const fairAxis = chart?.scales?.y23;
      if (!priceAxis || !fairAxis) return;

      // Copy computed numeric range (final values for this update).
      if (typeof priceAxis.min === 'number') fairAxis.min = priceAxis.min;
      if (typeof priceAxis.max === 'number') fairAxis.max = priceAxis.max;

      // Copy tick values so tick marks line up 1:1.
      if (Array.isArray(priceAxis.ticks) && priceAxis.ticks.length > 0) {
        fairAxis.ticks = priceAxis.ticks.map((t: any) => ({ ...t }));
      }

      // Copy internal mapping values used by getPixelForValue.
      if (typeof (priceAxis as any)._startValue === 'number') (fairAxis as any)._startValue = (priceAxis as any)._startValue;
      if (typeof (priceAxis as any)._endValue === 'number') (fairAxis as any)._endValue = (priceAxis as any)._endValue;
      if (typeof (priceAxis as any)._valueRange === 'number') (fairAxis as any)._valueRange = (priceAxis as any)._valueRange;

      // Critical for 1:1 pixel alignment: share the same pixel geometry.
      if (typeof (priceAxis as any).top === 'number') (fairAxis as any).top = (priceAxis as any).top;
      if (typeof (priceAxis as any).bottom === 'number') (fairAxis as any).bottom = (priceAxis as any).bottom;
      if (typeof (priceAxis as any).height === 'number') (fairAxis as any).height = (priceAxis as any).height;
      if (typeof (priceAxis as any)._startPixel === 'number') (fairAxis as any)._startPixel = (priceAxis as any)._startPixel;
      if (typeof (priceAxis as any)._endPixel === 'number') (fairAxis as any)._endPixel = (priceAxis as any)._endPixel;
      if (typeof (priceAxis as any)._length === 'number') (fairAxis as any)._length = (priceAxis as any)._length;
      if (typeof (priceAxis as any)._reversePixels === 'boolean') (fairAxis as any)._reversePixels = (priceAxis as any)._reversePixels;
      if (typeof (priceAxis as any)._alignToPixels === 'boolean') (fairAxis as any)._alignToPixels = (priceAxis as any)._alignToPixels;
    },
  };

  const syncOcfPriceAxisToPriceAxisPlugin = {
    id: 'syncOcfPriceAxisToPriceAxis',
    afterUpdate: (chart: any) => {
      // When Price + OCF Fair/Entry/Exit are visible, align y16 to Price axis (y).
      if (!chart) return;
      if (!priceVisible || !ocfPriceVisible) return;

      const priceAxis = chart?.scales?.y;
      const ocfAxis = chart?.scales?.y16;
      if (!priceAxis || !ocfAxis) return;

      if (typeof priceAxis.min === 'number') ocfAxis.min = priceAxis.min;
      if (typeof priceAxis.max === 'number') ocfAxis.max = priceAxis.max;

      if (Array.isArray(priceAxis.ticks) && priceAxis.ticks.length > 0) {
        ocfAxis.ticks = priceAxis.ticks.map((t: any) => ({ ...t }));
      }

      if (typeof (priceAxis as any)._startValue === 'number') (ocfAxis as any)._startValue = (priceAxis as any)._startValue;
      if (typeof (priceAxis as any)._endValue === 'number') (ocfAxis as any)._endValue = (priceAxis as any)._endValue;
      if (typeof (priceAxis as any)._valueRange === 'number') (ocfAxis as any)._valueRange = (priceAxis as any)._valueRange;

      if (typeof (priceAxis as any).top === 'number') (ocfAxis as any).top = (priceAxis as any).top;
      if (typeof (priceAxis as any).bottom === 'number') (ocfAxis as any).bottom = (priceAxis as any).bottom;
      if (typeof (priceAxis as any).height === 'number') (ocfAxis as any).height = (priceAxis as any).height;
      if (typeof (priceAxis as any)._startPixel === 'number') (ocfAxis as any)._startPixel = (priceAxis as any)._startPixel;
      if (typeof (priceAxis as any)._endPixel === 'number') (ocfAxis as any)._endPixel = (priceAxis as any)._endPixel;
      if (typeof (priceAxis as any)._length === 'number') (ocfAxis as any)._length = (priceAxis as any)._length;
      if (typeof (priceAxis as any)._reversePixels === 'boolean') (ocfAxis as any)._reversePixels = (priceAxis as any)._reversePixels;
      if (typeof (priceAxis as any)._alignToPixels === 'boolean') (ocfAxis as any)._alignToPixels = (priceAxis as any)._alignToPixels;
    },
  };

  const syncFairPriceAxisToFlatEpsAxisPlugin = {
    id: 'syncFairPriceAxisToFlatEpsAxis',
    afterUpdate: (chart: any) => {
      // Optional visual mode: when comparing Fair Price vs Flat EPS (ttm),
      // make y23 a scaled copy of y22 so equal-ratio points align vertically.
      // This does NOT couple to the Price axis; it couples y23 to y22.
      if (!chart) return;
      if (!flatEPSTTMVisible || !fairPriceVisible) return;

      // If Price is visible, prefer the connected Price↔Fair axes.
      if (priceVisible) return;

      // If the user is explicitly forcing a y23 range (Entry/Exit), don't override it.
      if (forcedPriceRange) return;

      const epsAxis = chart?.scales?.y22;
      const fairAxis = chart?.scales?.y23;
      if (!epsAxis || !fairAxis) return;

      const epsDataset = chart.data?.datasets?.find((ds: any) => ds?.label === 'Flat EPS (ttm)' && ds?.yAxisID === 'y22');
      const fairDataset = chart.data?.datasets?.find((ds: any) => ds?.label === 'Fair Price' && ds?.yAxisID === 'y23');
      if (!epsDataset || !fairDataset) return;

      const toMs = (x: any): number | null => {
        const t = new Date(x as any).getTime();
        return Number.isFinite(t) ? t : null;
      };

      const epsMap = new Map<number, number>();
      for (const p of (epsDataset.data ?? []) as any[]) {
        const xMs = toMs(p?.x);
        const y = Number(p?.y);
        if (xMs == null) continue;
        if (!Number.isFinite(y)) continue;
        epsMap.set(xMs, y);
      }

      const ratios: number[] = [];
      for (const p of (fairDataset.data ?? []) as any[]) {
        const xMs = toMs(p?.x);
        const fairY = Number(p?.y);
        if (xMs == null) continue;
        if (!Number.isFinite(fairY) || fairY === 0) continue;
        const epsY = epsMap.get(xMs);
        if (epsY == null || !Number.isFinite(epsY) || epsY === 0) continue;
        const r = fairY / epsY;
        if (Number.isFinite(r) && r > 0) ratios.push(r);
      }

      if (ratios.length === 0) return;
      ratios.sort((a, b) => a - b);
      const peMultiple = ratios[Math.floor(ratios.length / 2)];
      if (!Number.isFinite(peMultiple) || peMultiple <= 0) return;

      // Copy computed numeric range (final values for this update), scaled.
      if (typeof epsAxis.min === 'number') fairAxis.min = epsAxis.min * peMultiple;
      if (typeof epsAxis.max === 'number') fairAxis.max = epsAxis.max * peMultiple;

      // Copy tick values so tick marks line up 1:1 (scaled).
      if (Array.isArray(epsAxis.ticks) && epsAxis.ticks.length > 0) {
        fairAxis.ticks = epsAxis.ticks.map((t: any) => {
          const v = Number(t?.value);
          const scaledV = Number.isFinite(v) ? v * peMultiple : t?.value;
          const out: any = { ...t, value: scaledV };
          if (typeof t?.label === 'string') {
            const labelNum = Number(t.label);
            out.label = Number.isFinite(labelNum) ? formatAxisTickNumber(labelNum * peMultiple) : t.label;
          }
          return out;
        });
      }

      // Copy internal mapping values used by getPixelForValue (scaled).
      if (typeof (epsAxis as any)._startValue === 'number') (fairAxis as any)._startValue = (epsAxis as any)._startValue * peMultiple;
      if (typeof (epsAxis as any)._endValue === 'number') (fairAxis as any)._endValue = (epsAxis as any)._endValue * peMultiple;
      if (typeof (epsAxis as any)._valueRange === 'number') (fairAxis as any)._valueRange = (epsAxis as any)._valueRange * peMultiple;

      // Share the same pixel geometry.
      if (typeof (epsAxis as any).top === 'number') (fairAxis as any).top = (epsAxis as any).top;
      if (typeof (epsAxis as any).bottom === 'number') (fairAxis as any).bottom = (epsAxis as any).bottom;
      if (typeof (epsAxis as any).height === 'number') (fairAxis as any).height = (epsAxis as any).height;
      if (typeof (epsAxis as any)._startPixel === 'number') (fairAxis as any)._startPixel = (epsAxis as any)._startPixel;
      if (typeof (epsAxis as any)._endPixel === 'number') (fairAxis as any)._endPixel = (epsAxis as any)._endPixel;
      if (typeof (epsAxis as any)._length === 'number') (fairAxis as any)._length = (epsAxis as any)._length;
      if (typeof (epsAxis as any)._reversePixels === 'boolean') (fairAxis as any)._reversePixels = (epsAxis as any)._reversePixels;
      if (typeof (epsAxis as any)._alignToPixels === 'boolean') (fairAxis as any)._alignToPixels = (epsAxis as any)._alignToPixels;
    },
  };

  const autoFitAllYScalesPlugin = {
    id: 'autoFitAllYScales',
    afterDataLimits: (chart: any, args: any) => {
      const scale = args?.scale;
      if (!chart || !scale || scale.axis !== 'y') return;

       // y23 can be explicitly synced to y22 when comparing Flat EPS (ttm) vs Fair Price.
       // Skip only when no forced y23 range is active (Entry/Exit sliders).
       if (scale.id === 'y23' && (fairPriceVisible && flatEPSTTMVisible) && !forcedPriceRange) return;

      // If the axis has an explicit min/max configured, keep it fixed.
      // If an axis has explicit min/max configured, keep it fixed.
      const optionsMin = (scale as any)?.options?.min;
      const optionsMax = (scale as any)?.options?.max;
      const hasExplicitMinMax = (typeof optionsMin === 'number' || typeof optionsMax === 'number');

      const niceStep = (rawStep: number) => {
        const safe = Math.max(1e-12, Math.abs(rawStep));
        const exponent = Math.floor(Math.log10(safe));
        const base = Math.pow(10, exponent);
        const fraction = safe / base;

        let niceFraction = 1;
        if (fraction <= 1) niceFraction = 1;
        else if (fraction <= 2) niceFraction = 2;
        else if (fraction <= 2.5) niceFraction = 2.5;
        else if (fraction <= 5) niceFraction = 5;
        else niceFraction = 10;

        return niceFraction * base;
      };

      const roundDownToStep = (value: number, step: number) => Math.floor(value / step) * step;
      const roundUpToStep = (value: number, step: number) => Math.ceil(value / step) * step;

      // If min/max are explicitly set (e.g., forcedPriceRange from the Entry/Exit sliders),
      // Chart.js may add an extra "includeBounds" tick that isn't aligned to stepSize
      // (e.g., 700 and 720 with a 50 step). Snap those explicit bounds to stepSize.
      if (hasExplicitMinMax) {
        const configuredStepSize = Number((scale as any)?.options?.ticks?.stepSize);
        const step = (Number.isFinite(configuredStepSize) && configuredStepSize > 0)
          ? configuredStepSize
          : null;

        // Only enforce snapping on price-like axes where we expect clean increments.
        if (step && (scale.id === 'y' || scale.id === 'y23')) {
          const rawMin = (typeof optionsMin === 'number')
            ? optionsMin
            : (typeof (scale as any).min === 'number' ? (scale as any).min : null);
          const rawMax = (typeof optionsMax === 'number')
            ? optionsMax
            : (typeof (scale as any).max === 'number' ? (scale as any).max : null);

          if (typeof rawMin === 'number' && Number.isFinite(rawMin)) {
            (scale as any).min = roundDownToStep(rawMin, step);
          }
          if (typeof rawMax === 'number' && Number.isFinite(rawMax)) {
            (scale as any).max = roundUpToStep(rawMax, step);
          }

          // Prices can't be negative; clamp only if needed.
          if (scale.id === 'y' && typeof (scale as any).min === 'number') {
            (scale as any).min = Math.max(0, (scale as any).min);
          }
          if (
            typeof (scale as any).min === 'number' &&
            typeof (scale as any).max === 'number' &&
            (scale as any).max <= (scale as any).min
          ) {
            (scale as any).max = (scale as any).min + step;
          }
        }

        return;
      }

      // Find min/max based on *visible* points, optionally constrained to the visible x-range.
      const xScale = chart.scales?.x;
      const xMinVisible = typeof xScale?.min === 'number' ? xScale.min : Number.NEGATIVE_INFINITY;
      const xMaxVisible = typeof xScale?.max === 'number' ? xScale.max : Number.POSITIVE_INFINITY;

      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;

      const datasets: any[] = chart.data?.datasets ?? [];
      for (let datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
        const meta = chart.getDatasetMeta?.(datasetIndex);
        if (!meta || meta.hidden) continue;
        if (!meta.yScale || meta.yScale.id !== scale.id) continue;

        const parsed: any[] = meta._parsed ?? [];
        for (const p of parsed) {
          const x = typeof p?.x === 'number' ? p.x : NaN;
          const y = typeof p?.y === 'number' ? p.y : NaN;
          if (!Number.isFinite(y)) continue;
          if (Number.isFinite(x) && (x < xMinVisible || x > xMaxVisible)) continue;

          if (y < min) min = y;
          if (y > max) max = y;
        }
      }

      if (!Number.isFinite(min) || !Number.isFinite(max)) return;

      const range = max - min;
      let pad = range > 0 ? range * 0.1 : Math.max(1, Math.abs(min || max) * 0.1);

      // Price should feel tighter (users expect the line to sit near the top/bottom
      // with modest headroom). Cap padding so rounding/long ranges don't push max
      // to the next big round number like 300 when the visible max is ~273.
      if (scale.id === 'y') {
        const cap = Math.max(1, Math.abs(max) * 0.05);
        pad = Math.min(pad, cap);
      }

      const paddedMin = min - pad;
      const paddedMax = max + pad;

      // For Price, use the padded bounds directly (no "nice" rounding).
      // This prevents the axis from jumping to a larger round number (e.g., 300)
      // when the visible max is below that (e.g., 273).
      if (scale.id === 'y') {
        const configuredMaxTicks = Number((scale as any)?.options?.ticks?.maxTicksLimit);
        const targetTicks = (Number.isFinite(configuredMaxTicks) && configuredMaxTicks > 0)
          ? configuredMaxTicks
          : 12;

        const paddedRange = Math.max(0, paddedMax - paddedMin);
        const rawStep = paddedRange > 0
          ? paddedRange / Math.max(1, (targetTicks - 1))
          : Math.max(1, Math.abs(paddedMax || paddedMin) * 0.1);
        const step = niceStep(rawStep);
        if (!Number.isFinite(step) || step <= 0) return;

        // We allow slightly less than the target padding if it helps avoid
        // an extra top tick (e.g., prefer 300 over 310 when still >= data max).
        const rawMin = paddedMin;
        const rawMax = paddedMax;

        const maxDown = roundDownToStep(rawMax, step);
        const maxUp = roundUpToStep(rawMax, step);
        let priceMax = maxDown >= max ? maxDown : maxUp;

        const minDown = roundDownToStep(rawMin, step);
        const minUp = roundUpToStep(rawMin, step);
        // Keep bottom tight but never above the data min.
        let priceMin = minUp <= min ? minUp : minDown;

        // Prices can't be negative; clamp only if needed.
        priceMin = Math.max(0, priceMin);
        if (!Number.isFinite(priceMin) || !Number.isFinite(priceMax)) return;
        if (priceMax <= priceMin) priceMax = priceMin + step;

        scale.min = priceMin;
        scale.max = priceMax;
        return;
      }

      // Keep valuation ratios tight: enforce ~10% padding without extra rounding.
      if (scale.id === 'y2') {
        let outMin = paddedMin;
        let outMax = paddedMax;
        if (!Number.isFinite(outMin) || !Number.isFinite(outMax)) return;
        if (outMax <= outMin) outMax = outMin + 1;
        scale.min = outMin;
        scale.max = outMax;
        return;
      }

      // Choose a "nice" step so tick labels are rounded (avoid values like 99 or 303).
      const configuredMaxTicks = Number((scale as any)?.options?.ticks?.maxTicksLimit);
      const targetTicks = Number.isFinite(configuredMaxTicks) && configuredMaxTicks > 0
        ? configuredMaxTicks
        : 6;
      const paddedRange = Math.max(0, paddedMax - paddedMin);
      const rawStep = paddedRange > 0 ? paddedRange / Math.max(1, (targetTicks - 1)) : Math.max(1, Math.abs(paddedMax || paddedMin) * 0.1);
      let step = niceStep(rawStep);

      let niceMin = roundDownToStep(paddedMin, step);
      let niceMax = roundUpToStep(paddedMax, step);

      // If rounding expands the visible range far beyond the intended padding,
      // reduce the step to keep the chart from looking "pushed down".
      const tightenOvershoot = () => {
        const overshootLimit = scale.id === 'y' ? 0.35 : 1.5;
        for (let i = 0; i < 6; i++) {
          const overshootTop = niceMax - paddedMax;
          const overshootBottom = paddedMin - niceMin;
          const tooMuchTop = overshootTop > pad * overshootLimit;
          const tooMuchBottom = overshootBottom > pad * overshootLimit;
          if (!tooMuchTop && !tooMuchBottom) break;

          step = niceStep(step / 2);
          if (!Number.isFinite(step) || step <= 0) break;
          niceMin = roundDownToStep(paddedMin, step);
          niceMax = roundUpToStep(paddedMax, step);
        }

        // Final clamp (especially important for Price) so we don't end up
        // with too much headroom just due to rounding.
        const maxExtra = pad * (scale.id === 'y' ? 0.35 : 1.5);
        const minExtra = pad * (scale.id === 'y' ? 0.35 : 1.5);
        if (Number.isFinite(maxExtra) && maxExtra > 0) {
          niceMax = Math.max(paddedMax, Math.min(niceMax, paddedMax + maxExtra));
        }
        if (Number.isFinite(minExtra) && minExtra > 0) {
          niceMin = Math.min(paddedMin, Math.max(niceMin, paddedMin - minExtra));
        }
      };

      tightenOvershoot();

      // Avoid overly coarse rounding that wastes vertical space.
      // If we ended up with too few ticks, try a smaller step.
      const tickCount = (minV: number, maxV: number, s: number) => Math.floor((maxV - minV) / s + 0.5) + 1;
      let count = tickCount(niceMin, niceMax, step);
      if (count <= 4) {
        step = niceStep(step / 2);
        niceMin = roundDownToStep(paddedMin, step);
        niceMax = roundUpToStep(paddedMax, step);
        tightenOvershoot();
      }

      // Price axis should not go below 0.
      if (scale.id === 'y') {
        // Requirement: when Price + Fair Price are visible, both axes must start at 0.
        if (priceVisible && fairPriceVisible) {
          niceMin = 0;
        } else {
          niceMin = Math.max(0, niceMin);
        }
        scale.min = niceMin;
        scale.max = niceMax;
      } else {
        scale.min = niceMin;
        scale.max = niceMax;
      }
    },

  };

  const getLastFiniteYFromElementData = React.useCallback((data: any[]): number | null => {
    const xMinVisible = typeof effectiveXBounds?.min === 'number' ? effectiveXBounds.min : Number.NEGATIVE_INFINITY;
    const xMaxVisible = typeof effectiveXBounds?.max === 'number' ? effectiveXBounds.max : Number.POSITIVE_INFINITY;

    for (let i = data.length - 1; i >= 0; i--) {
      const p = data[i];
      const y = (p && typeof p === 'object' && 'y' in p) ? (p as any).y : p;
      if (!(typeof y === 'number' && Number.isFinite(y))) continue;

      // If an X value exists, ensure it is within the visible X-range.
      if (p && typeof p === 'object' && 'x' in p) {
        const xMs = new Date((p as any).x).getTime();
        if (!Number.isFinite(xMs)) continue;
        if (xMs < xMinVisible || xMs > xMaxVisible) continue;
      }

      return y;
    }
    return null;
  }, [effectiveXBounds]);

  return (
    <div 
      style={{ 
        height: '100%', 
        backgroundColor,
        borderRadius: '0.5rem',
        padding: '0rem 0rem',
        border: `1px solid ${gridColor}`,
        overflow: 'visible',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div 
        ref={chartContainerRef}
        style={{ 
          height: '100%', 
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Line
          ref={chartRef}
          data={{ labels, datasets }}
          options={options}
          plugins={[chartAreaTopBorderPlugin, normalizedPeOverlayPlugin, syncFairPriceAxisToPriceAxisPlugin, syncOcfPriceAxisToPriceAxisPlugin, syncFairPriceAxisToFlatEpsAxisPlugin, autoFitAllYScalesPlugin, chartAreaInsetsReporterPlugin]}
        />
        
        {/* Static Y-axis tooltips - always visible at last value position */}
        {(() => {
          // Get elements for checking visibility
          const priceElement = elements.find(e => e.id === 'price-line');
          const revenueElement = elements.find(e => e.id === 'revenue');
          const peElement = elements.find(e => e.id === 'pe');
          const epsElement = elements.find(e => e.id === 'eps-diluted');

          return (
            <>
              {/* Normalized PE tooltip - positioned on the Y2 axis at the dashed overlay */}
              {normalizedPEPixelY !== null && axisPositions.y2 > 0 && (
                <div
                  id="normalized-pe-axis-tooltip"
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y2}px`,
                    top: `${normalizedPEPixelY}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#000000',
                    border: '1px solid #000000',
                    borderRadius: '0.1875rem',
                    padding: '0.1125rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(Math.floor(normalizedPEOverlayValue! * 100) / 100).toFixed(2)}
                </div>
              )}

              {normalizedPOCFPixelY !== null && axisPositions.y2 > 0 && (
                <div
                  id="normalized-pocf-axis-tooltip"
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y2}px`,
                    top: `${normalizedPOCFPixelY}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: elements.find((element) => element.id === 'p-ocf-ratio' && element.visible)?.color ?? '#f472b6',
                    border: `1px solid ${elements.find((element) => element.id === 'p-ocf-ratio' && element.visible)?.color ?? '#f472b6'}`,
                    borderRadius: '0.1875rem',
                    padding: '0.1125rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(Math.floor(normalizedPOCFOverlayValue! * 100) / 100).toFixed(2)}
                </div>
              )}

              {/* Left Y-axis tooltip (Price) - positioned at last value on Y axis */}
              {priceElement?.visible && priceElement.data.length > 0 && lastValuePositions.price !== null && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y}px`,
                    top: `${lastValuePositions.price}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: tooltipBackgroundColor,
                    border: '1px solid rgba(59, 130, 246, 1)',
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: '#3b82f6',
                    fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementData(priceElement.data);
                    return (lastValue ?? 0).toFixed(2);
                  })()}
                </div>
              )}

              {/* Right Y1-axis tooltip (Revenue) - positioned at last value on Y1 axis */}
              {revenueElement?.visible && revenueElement.data.length > 0 && lastValuePositions.revenue !== null && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y1}px`,
                    top: `${lastValuePositions.revenue}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(34, 197, 94, 0.98)',
                    border: '1px solid rgba(34, 197, 94, 1)',
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementData(revenueElement.data) ?? 0;
                    return formatRevenueWithUnits(lastValue);
                  })()}
                </div>
              )}

              {/* Right Y1-axis tooltip (Gross Profit) - positioned at last value on Y1 axis */}
              {(() => {
                const grossProfitElement = elements.find(e => e.id === 'gross-profit');
                return grossProfitElement?.visible && grossProfitElement.data.length > 0 && lastValuePositions.grossProfit !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y1}px`,
                      top: `${lastValuePositions.grossProfit}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(139, 92, 246, 0.98)',
                      border: '1px solid rgba(139, 92, 246, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(grossProfitElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y1-axis tooltip (Operating Income) - positioned at last value on Y1 axis */}
              {(() => {
                const operatingIncomeElement = elements.find(e => e.id === 'operating-income');
                return operatingIncomeElement?.visible && operatingIncomeElement.data.length > 0 && lastValuePositions.operatingIncome !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y1}px`,
                      top: `${lastValuePositions.operatingIncome}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(234, 88, 12, 0.98)',
                      border: '1px solid rgba(234, 88, 12, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(operatingIncomeElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y1-axis tooltip (Pretax Income) - positioned at last value on Y1 axis */}
              {(() => {
                const pretaxIncomeElement = elements.find(e => e.id === 'pretax-income');
                return pretaxIncomeElement?.visible && pretaxIncomeElement.data.length > 0 && lastValuePositions.pretaxIncome !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y1}px`,
                      top: `${lastValuePositions.pretaxIncome}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(6, 182, 212, 0.98)',
                      border: '1px solid rgba(6, 182, 212, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.375rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(pretaxIncomeElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y2-axis tooltip (PE) - positioned at last value on Y2 axis */}
              {peElement?.visible && peElement.data.length > 0 && axisPositions.y2 > 0 && lastValuePositions.pe !== null && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y2}px`,
                    top: `${lastValuePositions.pe}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: tooltipBackgroundColor,
                    border: `1px solid ${isDarkMode ? '#EEEEEE' : 'rgba(0, 0, 0, 1)'}`,
                    borderRadius: '0.1875rem',
                    padding: '0.1125rem 0.25rem',
                    color: isDarkMode ? '#EEEEEE' : 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    width: `${RIGHT_AXIS_PE_TOOLTIP_WIDTH_PX}px`,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementData(peElement.data) ?? 0;
                    return (Math.floor(lastValue * 100) / 100).toFixed(2);
                  })()}
                </div>
              )}

              {/* Right Y2-axis tooltip (P/OCF) - positioned at last value on Y2 axis */}
              {(() => {
                const pOcfElement = elements.find(e => e.id === 'p-ocf-ratio');
                const shouldShow = pOcfElement?.visible && pOcfElement.data.length > 0 && axisPositions.y2 > 0 && lastValuePositions.pOcf !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y2}px`,
                      top: `${lastValuePositions.pOcf}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: pOcfElement?.color ?? tooltipBackgroundColor,
                      border: `1px solid ${pOcfElement?.color ?? 'rgba(0, 0, 0, 1)'}`,
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(pOcfElement!.data) ?? 0;
                      return (Math.floor(lastValue * 100) / 100).toFixed(2);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y2-axis tooltip (P/FCF) - positioned at last value on Y2 axis */}
              {(() => {
                const pFcfElement = elements.find(e => e.id === 'p-fcf-ratio');
                const shouldShow = pFcfElement?.visible && pFcfElement.data.length > 0 && axisPositions.y2 > 0 && lastValuePositions.pFcf !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y2}px`,
                      top: `${lastValuePositions.pFcf}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: pFcfElement?.color ?? tooltipBackgroundColor,
                      border: `1px solid ${pFcfElement?.color ?? 'rgba(0, 0, 0, 1)'}`,
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(pFcfElement!.data) ?? 0;
                      return (Math.floor(lastValue * 100) / 100).toFixed(2);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y15-axis tooltip (Flat OCF/Share (ttm)) - positioned at last value on Y15 axis */}
              {(() => {
                const flatOcfElement = elements.find(e => e.id === 'flat-ocf-ttm');
                const shouldShow = flatOcfElement?.visible && flatOcfElement.data.length > 0 && axisPositions.y15 > 0 && lastValuePositions.flatOCFTTM !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y15}px`,
                      top: `${lastValuePositions.flatOCFTTM}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: flatOcfElement?.color ?? 'rgba(34, 197, 94, 0.98)',
                      border: `1px solid ${flatOcfElement?.color ?? 'rgba(34, 197, 94, 1)'}`,
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(flatOcfElement!.data) ?? 0;
                      return lastValue.toFixed(2);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y16-axis tooltip (Analysis OCF Fair/Entry/Exit) - positioned at last value on Y16 axis */}
              {(() => {
                if (axisPositions.y16 <= 0) return null;

                const buildItem = (
                  id: string,
                  backgroundColor: string,
                  border: string,
                  pixelY: number | null
                ) => {
                  const element = elements.find(e => e.id === id);
                  const shouldShow = !!element?.visible && (element.data?.length ?? 0) > 0 && pixelY !== null;
                  if (!shouldShow) return null;

                  const lastValue = getLastFiniteYFromElementData(element!.data) ?? 0;
                  return (
                    <div
                      key={id}
                      style={{
                        position: 'absolute',
                        left: `${axisPositions.y16}px`,
                        top: `${pixelY}px`,
                        transform: 'translate(-50%, -50%)',
                        backgroundColor,
                        border,
                        borderRadius: '0.1875rem',
                        padding: '0.225rem 0.25rem',
                        color: 'white',
                        fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                        fontWeight: 'normal',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                      }}
                    >
                      {(() => {
                        const num = typeof lastValue === 'number' ? lastValue : Number(lastValue);
                        return Number.isFinite(num) ? num.toFixed(2) : '0.00';
                      })()}
                    </div>
                  );
                };

                const items = [
                  buildItem(
                    'fair-price-ocf',
                    'rgba(99, 102, 241, 0.98)',
                    '1px solid rgba(99, 102, 241, 1)',
                    lastValuePositions.fairPriceOcf
                  ),
                  buildItem(
                    'entry-point-1-ocf',
                    'rgba(14, 165, 233, 0.98)',
                    '1px solid rgba(14, 165, 233, 1)',
                    lastValuePositions.entryPriceOcf
                  ),
                  buildItem(
                    'entry-point-2-ocf',
                    'rgba(249, 115, 22, 0.98)',
                    '1px solid rgba(249, 115, 22, 1)',
                    lastValuePositions.exitPriceOcf
                  ),
                ].filter(Boolean);

                if (items.length === 0) return null;
                return <>{items}</>;
              })()}

              {/* Right Y3-axis tooltip (EPS) - positioned from the displayed value */}
              {(() => {
                if (!(epsElement?.visible && epsElement.data.length > 0 && axisPositions.y3 > 0)) return null;

                const lastValue = getLastFiniteYFromElementData(epsElement.data) ?? 0;
                const chart = chartRef.current || globalChartInstance;
                const y3 = chart?.scales?.y3;
                const topPx = (y3 && Number.isFinite(lastValue))
                  ? y3.getPixelForValue(lastValue)
                  : lastValuePositions.eps;

                if (!(typeof topPx === 'number' && Number.isFinite(topPx))) return null;

                return (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y3}px`,
                      top: `${topPx}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(16, 185, 129, 0.98)',
                      border: '1px solid rgba(16, 185, 129, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {lastValue.toFixed(2)}
                  </div>
                );
              })()}

              {/* Right Y4-axis tooltip (Cash) - positioned at last value on Y4 axis */}
              {(() => {
                const cashElement = elements.find(e => e.id === 'cash-equivalents');
                return cashElement?.visible && cashElement.data.length > 0 && axisPositions.y4 > 0 && lastValuePositions.cash !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y4}px`,
                      top: `${lastValuePositions.cash}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(34, 211, 238, 0.98)',
                      border: '1px solid rgba(34, 211, 238, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(cashElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y5-axis tooltip (Total Assets) - positioned at last value on Y5 axis */}
              {(() => {
                const totalAssetsElement = elements.find(e => e.id === 'total-assets');
                return totalAssetsElement?.visible && totalAssetsElement.data.length > 0 && axisPositions.y5 > 0 && lastValuePositions.totalAssets !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y5}px`,
                      top: `${lastValuePositions.totalAssets}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(20, 184, 166, 0.98)',
                      border: '1px solid rgba(20, 184, 166, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(totalAssetsElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y5');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y6-axis tooltip (Short-Term Debt) - positioned at last value on Y6 axis */}
              {(() => {
                const shortTermDebtElement = elements.find(e => e.id === 'short-term-debt');
                return shortTermDebtElement?.visible && shortTermDebtElement.data.length > 0 && axisPositions.y6 > 0 && lastValuePositions.shortTermDebt !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y6}px`,
                      top: `${lastValuePositions.shortTermDebt}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(168, 85, 247, 0.98)',
                      border: '1px solid rgba(168, 85, 247, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(shortTermDebtElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y6');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y7-axis tooltip (Long-Term Debt) - positioned at last value on Y7 axis */}
              {(() => {
                const longTermDebtElement = elements.find(e => e.id === 'long-term-debt');
                return longTermDebtElement?.visible && longTermDebtElement.data.length > 0 && axisPositions.y7 > 0 && lastValuePositions.longTermDebt !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y7}px`,
                      top: `${lastValuePositions.longTermDebt}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(239, 68, 68, 0.98)',
                      border: '1px solid rgba(239, 68, 68, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(longTermDebtElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y7');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y8-axis tooltip (Total Liabilities) - positioned at last value on Y8 axis */}
              {(() => {
                const totalLiabilitiesElement = elements.find(e => e.id === 'total-liabilities');
                return totalLiabilitiesElement?.visible && totalLiabilitiesElement.data.length > 0 && axisPositions.y8 > 0 && lastValuePositions.totalLiabilities !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y8}px`,
                      top: `${lastValuePositions.totalLiabilities}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(99, 102, 241, 0.98)',
                      border: '1px solid rgba(99, 102, 241, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(totalLiabilitiesElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y8');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y9-axis tooltip (Shareholders Equity) - positioned at last value on Y9 axis */}
              {(() => {
                const shareholdersEquityElement = elements.find(e => e.id === 'shareholders-equity');
                return shareholdersEquityElement?.visible && shareholdersEquityElement.data.length > 0 && axisPositions.y9 > 0 && lastValuePositions.shareholdersEquity !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y9}px`,
                      top: `${lastValuePositions.shareholdersEquity}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(5, 150, 105, 0.98)',
                      border: '1px solid rgba(5, 150, 105, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(shareholdersEquityElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y9');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y10-axis tooltip (Operating Cash Flow) - positioned at last value on Y10 axis */}
              {(() => {
                const operatingCashFlowElement = elements.find(e => e.id === 'operating-cash-flow');
                const shouldShow = operatingCashFlowElement?.visible && (operatingCashFlowElement.data?.length ?? 0) > 0 && axisPositions.y10 > 0 && lastValuePositions.operatingCashFlow !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y10}px`,
                      top: `${lastValuePositions.operatingCashFlow}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(245, 158, 11, 0.98)',
                      border: '1px solid rgba(245, 158, 11, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(operatingCashFlowElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y11-axis tooltip (Free Cash Flow) - positioned at last value on Y11 axis */}
              {(() => {
                const freeCashFlowElement = elements.find(e => e.id === 'free-cash-flow');
                const shouldShow = freeCashFlowElement?.visible && (freeCashFlowElement.data?.length ?? 0) > 0 && axisPositions.y11 > 0 && lastValuePositions.freeCashFlow !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y11}px`,
                      top: `${lastValuePositions.freeCashFlow}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(16, 185, 129, 0.98)',
                      border: '1px solid rgba(16, 185, 129, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(freeCashFlowElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y10');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y12-axis tooltip (Dividends) - positioned at last value on Y12 axis */}
              {(() => {
                const dividendsElement = elements.find(e => e.id === 'dividends');
                const shouldShow = dividendsElement?.visible && (dividendsElement.data?.length ?? 0) > 0 && axisPositions.y12 > 0 && lastValuePositions.dividends !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y12}px`,
                      top: `${lastValuePositions.dividends}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(147, 51, 234, 0.98)',
                      border: '1px solid rgba(147, 51, 234, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(dividendsElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y12');
                    })()}
                  </div>
                );
              })()}

              {/* Right Y13-axis tooltip (Buybacks) - positioned at last value on Y13 axis */}
              {(() => {
                const buybacksElement = elements.find(e => e.id === 'buybacks');
                const shouldShow = buybacksElement?.visible && (buybacksElement.data?.length ?? 0) > 0 && axisPositions.y13 > 0 && lastValuePositions.buybacks !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y13}px`,
                      top: `${lastValuePositions.buybacks}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(20, 184, 166, 0.98)',
                      border: '1px solid rgba(20, 184, 166, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(buybacksElement.data) ?? 0;
                      return formatRevenueForAxis(lastValue, 'y13');
                    })()}
                  </div>
                );
              })()}

              {(() => {
                const y14AxisCenterPx = axisPositions.y14;

                return (
                  <>
                    {/* Right Y14-axis tooltip (Gross Profit Margin) - positioned at last value on Y14 axis */}
                    {(() => {
                      const grossProfitMarginElement = elements.find(e => e.id === 'gross-profit-margin');
                      const shouldShow = grossProfitMarginElement?.visible && (grossProfitMarginElement.data?.length ?? 0) > 0 && axisPositions.y14 > 0 && lastValuePositions.grossProfitMargin !== null;
                      return shouldShow && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${y14AxisCenterPx}px`,
                            top: `${lastValuePositions.grossProfitMargin}px`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(236, 72, 153, 0.98)',
                            border: '1px solid rgba(236, 72, 153, 1)',
                            borderRadius: '0.1875rem',
                            padding: '0.225rem 0.25rem',
                            color: 'white',
                            fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}
                        >
                          {(() => {
                            const lastValue = getLastFiniteYFromElementData(grossProfitMarginElement.data) ?? 0;
                            return lastValue.toFixed(2) + '%';
                          })()}
                        </div>
                      );
                    })()}

                    {/* Right Y15-axis tooltip (Operating Margin) - positioned at last value on Y15 axis */}
                    {(() => {
                      const operatingMarginElement = elements.find(e => e.id === 'operating-margin');
                      const shouldShow = operatingMarginElement?.visible && (operatingMarginElement.data?.length ?? 0) > 0 && axisPositions.y14 > 0 && lastValuePositions.operatingMargin !== null;
                      return shouldShow && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${y14AxisCenterPx}px`,
                            top: `${lastValuePositions.operatingMargin}px`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(139, 92, 246, 0.98)',
                            border: '1px solid rgba(139, 92, 246, 1)',
                            borderRadius: '0.1875rem',
                            padding: '0.225rem 0.25rem',
                            color: 'white',
                            fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}
                        >
                          {(() => {
                            const lastValue = getLastFiniteYFromElementData(operatingMarginElement.data) ?? 0;
                            return lastValue.toFixed(2) + '%';
                          })()}
                        </div>
                      );
                    })()}

                    {/* Right Y16-axis tooltip (Net Margin) - positioned at last value on Y16 axis */}
                    {(() => {
                      const netMarginElement = elements.find(e => e.id === 'net-margin');
                      const shouldShow = netMarginElement?.visible && (netMarginElement.data?.length ?? 0) > 0 && axisPositions.y14 > 0 && lastValuePositions.netMargin !== null;
                      return shouldShow && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${y14AxisCenterPx}px`,
                            top: `${lastValuePositions.netMargin}px`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(6, 182, 212, 0.98)',
                            border: '1px solid rgba(6, 182, 212, 1)',
                            borderRadius: '0.1875rem',
                            padding: '0.225rem 0.25rem',
                            color: 'white',
                            fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}
                        >
                          {(() => {
                            const lastValue = getLastFiniteYFromElementData(netMarginElement.data) ?? 0;
                            return lastValue.toFixed(2) + '%';
                          })()}
                        </div>
                      );
                    })()}

                    {/* Right Y17-axis tooltip (Operating CF Margin) - positioned at last value on Y17 axis */}
                    {(() => {
                      const operatingCFMarginElement = elements.find(e => e.id === 'operating-cf-margin');
                      const shouldShow = operatingCFMarginElement?.visible && (operatingCFMarginElement.data?.length ?? 0) > 0 && axisPositions.y14 > 0 && lastValuePositions.operatingCFMargin !== null;
                      return shouldShow && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${y14AxisCenterPx}px`,
                            top: `${lastValuePositions.operatingCFMargin}px`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(245, 158, 11, 0.98)',
                            border: '1px solid rgba(245, 158, 11, 1)',
                            borderRadius: '0.1875rem',
                            padding: '0.225rem 0.25rem',
                            color: 'white',
                            fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}
                        >
                          {(() => {
                            const lastValue = getLastFiniteYFromElementData(operatingCFMarginElement.data) ?? 0;
                            return lastValue.toFixed(2) + '%';
                          })()}
                        </div>
                      );
                    })()}

                    {/* Right Y18-axis tooltip (Free CF Margin) - positioned at last value on Y18 axis */}
                    {(() => {
                      const freeCFMarginElement = elements.find(e => e.id === 'free-cf-margin');
                      const shouldShow = freeCFMarginElement?.visible && (freeCFMarginElement.data?.length ?? 0) > 0 && axisPositions.y14 > 0 && lastValuePositions.freeCFMargin !== null;
                      return shouldShow && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${y14AxisCenterPx}px`,
                            top: `${lastValuePositions.freeCFMargin}px`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(132, 204, 22, 0.98)',
                            border: '1px solid rgba(132, 204, 22, 1)',
                            borderRadius: '0.1875rem',
                            padding: '0.225rem 0.25rem',
                            color: 'white',
                            fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}
                        >
                          {(() => {
                            const lastValue = getLastFiniteYFromElementData(freeCFMarginElement.data) ?? 0;
                            return lastValue.toFixed(2) + '%';
                          })()}
                        </div>
                      );
                    })()}
                  </>
                );
              })()}

              {/* Right Y19-axis tooltip (OCF to Net Income) - positioned at last value on Y19 axis */}
              {(() => {
                const ocfToNetIncomeElement = elements.find(e => e.id === 'operating-cf-to-ni');
                const shouldShow = ocfToNetIncomeElement?.visible && (ocfToNetIncomeElement.data?.length ?? 0) > 0 && axisPositions.y19 > 0 && lastValuePositions.ocfToNetIncome !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y19}px`,
                      top: `${lastValuePositions.ocfToNetIncome}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(139, 92, 246, 0.98)',
                      border: '1px solid rgba(139, 92, 246, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(ocfToNetIncomeElement.data) ?? 0;
                      return lastValue.toFixed(1) + '%';
                    })()}
                  </div>
                );
              })()}

              {/* Right Y20-axis tooltip (FCF to Net Income) - positioned at last value on Y20 axis */}
              {(() => {
                const fcfToNetIncomeElement = elements.find(e => e.id === 'free-cf-to-ni');
                const shouldShow = fcfToNetIncomeElement?.visible && (fcfToNetIncomeElement.data?.length ?? 0) > 0 && axisPositions.y20 > 0 && lastValuePositions.fcfToNetIncome !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y20}px`,
                      top: `${lastValuePositions.fcfToNetIncome}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(20, 184, 166, 0.98)',
                      border: '1px solid rgba(20, 184, 166, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(fcfToNetIncomeElement.data) ?? 0;
                      return lastValue.toFixed(2) + '%';
                    })()}
                  </div>
                );
              })()}

              {/* Right Y21-axis tooltip (ROIC) - positioned at last value on Y21 axis */}
              {(() => {
                const roicElement = elements.find(e => e.id === 'roic');
                const shouldShow = roicElement?.visible && (roicElement.data?.length ?? 0) > 0 && axisPositions.y21 > 0 && lastValuePositions.roic !== null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y21}px`,
                      top: `${lastValuePositions.roic}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(220, 38, 38, 0.98)',
                      border: '1px solid rgba(220, 38, 38, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(roicElement.data) ?? 0;
                      return lastValue.toFixed(2) + '%';
                    })()}
                  </div>
                );
              })()}
              
              {/* Right Y22-axis tooltip (Flat EPS (ttm)) - positioned at last value on Y22 axis */}
              {(() => {
                const flatEPSElement = elements.find(e => e.id === 'flat-eps-ttm');
                const shouldShow = !!flatEPSElement?.visible && (flatEPSElement.data?.length ?? 0) > 0 && axisPositions.y22 > 0;
                const badgeTop = lastValuePositions.flatEPSTTM;
                if (badgeTop === null || badgeTop === undefined) return null;
                return shouldShow && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y22}px`,
                      top: `${badgeTop}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(249, 115, 22, 0.98)',
                      border: '1px solid rgba(249, 115, 22, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementData(flatEPSElement!.data) ?? 0;
                      return lastValue.toFixed(2);
                    })()}
                  </div>
                );
              })()}

              {/* Right Y23-axis tooltip (Fair Price) - positioned at last value on Y23 axis */}
              {(() => {
                if (axisPositions.y23 <= 0) return null;

                const buildItem = (
                  id: string,
                  backgroundColor: string,
                  border: string,
                  pixelY: number | null
                ) => {
                  const element = elements.find(e => e.id === id);
                  const shouldShow = !!element?.visible && (element.data?.length ?? 0) > 0 && pixelY !== null;
                  if (!shouldShow) return null;

                  const lastValue = getLastFiniteYFromElementData(element!.data) ?? 0;
                  return (
                    <div
                      key={id}
                      style={{
                        position: 'absolute',
                        left: `${axisPositions.y23}px`,
                        top: `${pixelY}px`,
                        transform: 'translate(-50%, -50%)',
                        backgroundColor,
                        border,
                        borderRadius: '0.1875rem',
                        padding: '0.225rem 0.25rem',
                        color: 'white',
                        fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                        fontWeight: 'normal',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                      }}
                    >
                        {(() => {
                          const num = typeof lastValue === 'number' ? lastValue : Number(lastValue);
                          return Number.isFinite(num) ? num.toFixed(2) : '0.00';
                        })()}
                    </div>
                  );
                };

                const items = [
                  buildItem(
                    'fair-price',
                    'rgba(139, 92, 246, 0.98)',
                    '1px solid rgba(139, 92, 246, 1)',
                    lastValuePositions.fairPrice
                  ),
                  buildItem(
                    'fair-price-flat-eps-ttm',
                    'rgba(6, 182, 212, 0.98)',
                    '1px solid rgba(6, 182, 212, 1)',
                    lastValuePositions.fairPriceFlatEPSTTM
                  ),
                  buildItem(
                    'entry-point-1',
                    'rgba(16, 185, 129, 0.98)',
                    '1px solid rgba(16, 185, 129, 1)',
                    lastValuePositions.entryPrice
                  ),
                  buildItem(
                    'entry-point-2',
                    'rgba(239, 68, 68, 0.98)',
                    '1px solid rgba(239, 68, 68, 1)',
                    lastValuePositions.exitPrice
                  ),
                ].filter(Boolean);

                if (items.length === 0) return null;
                return <>{items}</>;
              })()}
            </>
          );
        })()}
        
        {/* Dynamic tooltips when mouse is over chart */}
        {tooltipData && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 10002,
            }}
          >
            {/* Left Y-axis dynamic tooltip (Price) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'price-line' && e.visible) && tooltipData.pricePixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(59, 130, 246, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisPrice.toFixed(2)}
              </div>
            )}

            {/* Right Y1-axis dynamic tooltip (Revenue) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'revenue' && e.visible) && tooltipData.revenuePixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y1}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(34, 197, 94, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisRevenue)}
              </div>
            )}

            {/* Right Y1-axis dynamic tooltip (Gross Profit) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'gross-profit' && e.visible) && tooltipData.grossProfitPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y1}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(139, 92, 246, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisRevenue)}
              </div>
            )}

            {/* Right Y1-axis dynamic tooltip (Operating Income) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'operating-income' && e.visible) && tooltipData.operatingIncomePixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y1}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(234, 88, 12, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisRevenue)}
              </div>
            )}

            {/* Right Y1-axis dynamic tooltip (Pretax Income) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'pretax-income' && e.visible) && tooltipData.pretaxIncomePixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y1}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(6, 182, 212, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisRevenue)}
              </div>
            )}

            {/* Right Y1-axis dynamic tooltip (Net Income) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'net-income' && e.visible) && tooltipData.netIncomePixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y1}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(245, 158, 11, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisRevenue)}
              </div>
            )}

            {/* Right Y2-axis dynamic tooltip (PE) - shows Y-axis value at mouse position */}
            {(() => {
              const shouldShow = elements.some(e => (e.id === 'pe' || e.id === 'p-ocf-ratio' || e.id === 'p-fcf-ratio') && e.visible);
              if (!shouldShow) return null;
              const fallbackRight = typeof tooltipData.chartArea?.right === 'number' ? tooltipData.chartArea.right : 0;
              const y2AxisX = axisPositions.y2 > 0 ? axisPositions.y2 : fallbackRight;
              if (!y2AxisX) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${y2AxisX}px`,
                    top: `${tooltipData.mouseY}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: tooltipBackgroundColor,
                    border: '1px solid rgba(0, 0, 0, 1)',
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(Math.floor(tooltipData.yAxisPE * 100) / 100).toFixed(2)}
                </div>
              );
            })()}

            {/* Right Y3-axis dynamic tooltip (EPS) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'eps-diluted' && e.visible) && axisPositions.y3 > 0 && tooltipData.epsPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y3}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(16, 185, 129, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisEps.toFixed(2)}
              </div>
            )}

            {/* Right Y4-axis dynamic tooltip (Cash) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'cash-equivalents' && e.visible) && axisPositions.y4 > 0 && tooltipData.cashPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y4}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(34, 211, 238, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisCash)}
              </div>
            )}

            {/* Right Y5-axis dynamic tooltip (Total Assets) - shows Y-axis value at mouse position */}
            {elements.some(e => e.id === 'total-assets' && e.visible) && axisPositions.y5 > 0 && tooltipData.totalAssetsPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y5}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(20, 184, 166, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisTotalAssets, 'y5')}
              </div>
            )}

            {/* Right Y6-axis tooltip (Short-Term Debt) */}
            {elements.some(e => e.id === 'short-term-debt' && e.visible) && axisPositions.y6 > 0 && tooltipData.shortTermDebtPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y6}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(168, 85, 247, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisShortTermDebt, 'y6')}
              </div>
            )}

            {/* Right Y7-axis tooltip (Long-Term Debt) */}
            {elements.some(e => e.id === 'long-term-debt' && e.visible) && axisPositions.y7 > 0 && tooltipData.longTermDebtPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y7}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(239, 68, 68, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisLongTermDebt, 'y7')}
              </div>
            )}

            {/* Right Y8-axis tooltip (Total Liabilities) */}
            {elements.some(e => e.id === 'total-liabilities' && e.visible) && axisPositions.y8 > 0 && tooltipData.totalLiabilitiesPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y8}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(99, 102, 241, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisTotalLiabilities, 'y8')}
              </div>
            )}

            {/* Right Y9-axis tooltip (Shareholders Equity) */}
            {elements.some(e => e.id === 'shareholders-equity' && e.visible) && axisPositions.y9 > 0 && tooltipData.shareholdersEquityPixelY !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y9}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(5, 150, 105, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisShareholdersEquity, 'y9')}
              </div>
            )}

            {/* Right Y10-axis tooltip (Operating Cash Flow) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'operating-cash-flow' && e.visible) && axisPositions.y10 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y10}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(245, 158, 11, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                    fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueWithUnits(tooltipData.yAxisOperatingCashFlow)}
              </div>
              );
            })()}

            {/* Right Y11-axis tooltip (Free Cash Flow) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'free-cash-flow' && e.visible) && axisPositions.y11 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y11}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(16, 185, 129, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisFreeCashFlow, 'y10')}
              </div>
              );
            })()}

            {/* Right Y12-axis tooltip (Dividends) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'dividends' && e.visible) && axisPositions.y12 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y12}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(147, 51, 234, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisDividends, 'y12')}
              </div>
              );
            })()}

            {/* Right Y13-axis tooltip (Buybacks) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'buybacks' && e.visible) && axisPositions.y13 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y13}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(20, 184, 166, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {formatRevenueForAxis(tooltipData.yAxisBuybacks, 'y13')}
              </div>
              );
            })()}

            {/* Right Y14-axis tooltip (Gross Profit Margin) */}
            {(() => {
              const shouldShow = elements.some(e => (
                e.id === 'gross-profit-margin' ||
                e.id === 'operating-margin' ||
                e.id === 'net-margin' ||
                e.id === 'operating-cf-margin' ||
                e.id === 'free-cf-margin'
              ) && e.visible) && axisPositions.y14 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y14}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: tooltipBackgroundColor,
                  border: '1px solid rgba(236, 72, 153, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisGrossProfitMargin.toFixed(2) + '%'}
              </div>
              );
            })()}

            {/* Right Y15-axis tooltip (Flat OCF/Share TTM) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'flat-ocf-ttm' && e.visible);
              if (!shouldShow) return null;
              const fallbackRight = typeof tooltipData.chartArea?.right === 'number' ? tooltipData.chartArea.right : 0;
              const y15AxisX = axisPositions.y15 > 0 ? axisPositions.y15 : fallbackRight;
              if (!y15AxisX) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${y15AxisX}px`,
                    top: `${tooltipData.mouseY}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: tooltipBackgroundColor,
                    border: '1px solid rgba(34, 197, 94, 1)',
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {tooltipData.yAxisFlatOCFTTM.toFixed(2)}
                </div>
              );
            })()}

            {/* Right Y16-axis tooltip (Analysis OCF) */}
            {(() => {
              const shouldShow = elements.some(e => (
                e.id === 'fair-price-ocf' ||
                e.id === 'entry-point-1-ocf' ||
                e.id === 'entry-point-2-ocf'
              ) && e.visible);
              if (!shouldShow) return null;
              const fallbackRight = typeof tooltipData.chartArea?.right === 'number' ? tooltipData.chartArea.right : 0;
              const y16AxisX = axisPositions.y16 > 0 ? axisPositions.y16 : fallbackRight;
              if (!y16AxisX) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${y16AxisX}px`,
                    top: `${tooltipData.mouseY}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: tooltipBackgroundColor,
                    border: '1px solid rgba(99, 102, 241, 1)',
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {tooltipData.yAxisFairPriceOCF.toFixed(2)}
                </div>
              );
            })()}

            {/* Right Y17-axis tooltip (Operating CF Margin) */}
            {(() => {
              const shouldShow = false;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y17}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(75, 85, 99, 0.98)',
                  border: '1px solid rgba(245, 158, 11, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisOperatingCFMargin.toFixed(2) + '%'}
              </div>
              );
            })()}

            {/* Right Y18-axis tooltip (Free CF Margin) */}
            {(() => {
              const shouldShow = false;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y18}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(75, 85, 99, 0.98)',
                  border: '1px solid rgba(132, 204, 22, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisFreeCFMargin.toFixed(2) + '%'}
              </div>
              );
            })()}

            {/* Right Y19-axis tooltip (OCF to Net Income) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'operating-cf-to-ni' && e.visible) && axisPositions.y19 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y19}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(75, 85, 99, 0.98)',
                  border: '1px solid rgba(139, 92, 246, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisOCFToNetIncome.toFixed(2) + '%'}
              </div>
              );
            })()}

            {/* Right Y20-axis tooltip (FCF to Net Income) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'free-cf-to-ni' && e.visible) && axisPositions.y20 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y20}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(75, 85, 99, 0.98)',
                  border: '1px solid rgba(20, 184, 166, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisFCFToNetIncome.toFixed(2) + '%'}
              </div>
              );
            })()}

            {/* Right Y21-axis tooltip (ROIC) */}
            {(() => {
              const shouldShow = elements.some(e => e.id === 'roic' && e.visible) && axisPositions.y21 > 0;
              return shouldShow && (
                <div
                style={{
                  position: 'absolute',
                  left: `${axisPositions.y21}px`,
                  top: `${tooltipData.mouseY}px`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(75, 85, 99, 0.98)',
                  border: '1px solid rgba(220, 38, 38, 1)',
                  borderRadius: '0.1875rem',
                  padding: '0.225rem 0.25rem',
                  color: 'white',
                  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}
              >
                {tooltipData.yAxisROIC.toFixed(1) + '%'}
              </div>
              );
            })()}

            {/* Right Y22-axis dynamic tooltips (Flat EPS (ttm)) */}
            {(() => {
              if (axisPositions.y22 <= 0) return null;

              const hoveredId = tooltipData.hoveredElementId ?? null;
              const hoveredValue = tooltipData.hoveredElementValue;

              const clampedTop = (v: number) => {
                const top = tooltipData.chartArea?.top ?? 0;
                const bottom = tooltipData.chartArea?.bottom ?? 0;
                if (bottom > top) return Math.max(top, Math.min(bottom, v));
                return v;
              };

              const isHoveringY22Segment = hoveredId === 'flat-eps-ttm';

              // When not on a y22 segment: show axis value at cursor height (single tooltip).
              if (!isHoveringY22Segment) {
                return (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y22}px`,
                      top: `${clampedTop(tooltipData.mouseY)}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(75, 85, 99, 0.98)',
                      border: '1px solid rgba(107, 114, 128, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                    {(tooltipData.yAxisFlatEPSTTM ?? 0).toFixed(2)}
                  </div>
                );
              }

              // When on a y22 segment: show the hovered series value, but still as a single tooltip.
              const resolveHoveredPixelY = () => {
                if (hoveredId === 'flat-eps-ttm') return tooltipData.flatEPSTTMPixelY;
                return null;
              };

              const resolveHoveredBorder = () => {
                return '1px solid rgba(249, 115, 22, 1)';
              };

              const resolveHoveredValue = () => {
                if (hoveredValue !== null && hoveredValue !== undefined) return hoveredValue;
                return tooltipData.graphFlatEPSTTM || 0;
              };

              const hoveredPixelY = resolveHoveredPixelY();
              if (typeof hoveredPixelY !== 'number' || !Number.isFinite(hoveredPixelY)) return null;

              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y22}px`,
                    top: `${clampedTop(hoveredPixelY)}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(75, 85, 99, 0.98)',
                    border: resolveHoveredBorder(),
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 10001,
                    pointerEvents: 'none',
                  }}
                >
                      {resolveHoveredValue().toFixed(2)}
                </div>
              );
            })()}

            {/* Right Y23-axis dynamic tooltips (Fair Price / Entry / Exit) */}
            {(() => {
              if (axisPositions.y23 <= 0) return null;

              const isY23SeriesVisible = elements.some(
                e => (
                  e.id === 'fair-price' ||
                  e.id === 'fair-price-flat-eps-ttm' ||
                  e.id === 'entry-point-1' ||
                  e.id === 'entry-point-2'
                ) && e.visible
              );
              if (!isY23SeriesVisible) return null;

              const hoveredId = tooltipData.hoveredElementId ?? null;
              const hoveredValue = tooltipData.hoveredElementValue;

              const clampedTop = (v: number) => {
                const top = tooltipData.chartArea?.top ?? 0;
                const bottom = tooltipData.chartArea?.bottom ?? 0;
                if (bottom > top) return Math.max(top, Math.min(bottom, v));
                return v;
              };

              const isHoveringY23Segment = (
                hoveredId === 'fair-price' ||
                hoveredId === 'fair-price-flat-eps-ttm' ||
                hoveredId === 'entry-point-1' ||
                hoveredId === 'entry-point-2'
              );

              // Default behavior: show the axis value at cursor height (moves with mouse).
              if (!isHoveringY23Segment) {
                return (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y23}px`,
                      top: `${clampedTop(tooltipData.mouseY)}px`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(75, 85, 99, 0.98)',
                      border: '1px solid rgba(107, 114, 128, 1)',
                      borderRadius: '0.1875rem',
                      padding: '0.225rem 0.25rem',
                      color: 'white',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999,
                      pointerEvents: 'none',
                    }}
                  >
                      {(() => {
                        const v = tooltipData.yAxisFairPrice;
                        const num = typeof v === 'number' ? v : Number(v);
                        return Number.isFinite(num) ? num.toFixed(2) : '0.00';
                      })()}
                  </div>
                );
              }

              const activeId = hoveredId;

              const activePixelY = activeId === 'fair-price'
                ? tooltipData.fairPricePixelY
                : (activeId === 'fair-price-flat-eps-ttm'
                  ? tooltipData.fairPriceFlatEPSTTMPixelY
                  : (activeId === 'entry-point-1'
                    ? tooltipData.entryPricePixelY
                    : tooltipData.exitPricePixelY));

              if (typeof activePixelY !== 'number' || !Number.isFinite(activePixelY)) return null;

              const border = activeId === 'fair-price'
                ? '1px solid rgba(139, 92, 246, 1)'
                : (activeId === 'fair-price-flat-eps-ttm'
                  ? '1px solid rgba(6, 182, 212, 1)'
                  : (activeId === 'entry-point-1'
                    ? '1px solid rgba(16, 185, 129, 1)'
                    : '1px solid rgba(239, 68, 68, 1)'));

              const value = (
                hoveredValue !== null && hoveredValue !== undefined
                  ? hoveredValue
                  : (activeId === 'fair-price'
                    ? (tooltipData.graphFairPrice || 0)
                    : (activeId === 'fair-price-flat-eps-ttm'
                      ? (tooltipData.graphFairPriceFlatEPSTTM || 0)
                      : (activeId === 'entry-point-1'
                        ? (tooltipData.graphEntryPrice || 0)
                        : (tooltipData.graphExitPrice || 0))))
              );

              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y23}px`,
                    top: `${clampedTop(activePixelY)}px`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(75, 85, 99, 0.98)',
                    border,
                    borderRadius: '0.1875rem',
                    padding: '0.225rem 0.25rem',
                    color: 'white',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    const num = typeof value === 'number' ? value : Number(value);
                    return Number.isFinite(num) ? num.toFixed(2) : '0.00';
                  })()}
                </div>
              );
            })()}

            {/* Bottom X-axis tooltip (Date) */}
            <div
              style={{
                position: 'absolute',
                left: `${tooltipData.mouseX}px`,
                bottom: '6px',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(75, 85, 99, 0.98)',
                border: '1px solid rgba(107, 114, 128, 1)',
                borderRadius: '0.1875rem',
                padding: '0.225rem 0.375rem',
                color: 'white',
                fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                fontWeight: 'normal',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                zIndex: 10000,
                pointerEvents: 'none',
              }}
            >
              {formatXAxisTooltipDate(tooltipData.date)}
            </div>
          </div>
        )}

        {/* Fixed Last Value Labels - Always visible on Y-axes */}
        {(() => {
          const xMinVisible = typeof effectiveXBounds?.min === 'number' ? effectiveXBounds.min : Number.NEGATIVE_INFINITY;
          const xMaxVisible = typeof effectiveXBounds?.max === 'number' ? effectiveXBounds.max : Number.POSITIVE_INFINITY;

          const getLastFiniteYFromElementDataLocal = (data: any[]): number | null => {
            for (let i = data.length - 1; i >= 0; i--) {
              const p = data[i];
              const y = (p && typeof p === 'object' && 'y' in p) ? (p as any).y : p;
              if (!(typeof y === 'number' && Number.isFinite(y))) continue;

              if (p && typeof p === 'object' && 'x' in p) {
                const xMs = new Date((p as any).x).getTime();
                if (!Number.isFinite(xMs)) continue;
                if (xMs < xMinVisible || xMs > xMaxVisible) continue;
              }

              return y;
            }
            return null;
          };

          // Get last values from elements
          const priceElement = elements.find(e => e.id === 'price-line');
          const revenueElement = elements.find(e => e.id === 'revenue');
          const peElement = elements.find(e => e.id === 'pe');

          return (
            <>
              {/* Price Last Value */}
              {priceElement?.visible && priceElement.data.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y}px`,
                    top: lastValuePositions.price !== null 
                      ? `${lastValuePositions.price}px`
                      : (() => {
                          // Fallback calculation while chart is loading
                          if (fixedChartDimensions.current.height === 0) return '0px';
                          const lastValue = getLastFiniteYFromElementDataLocal(priceElement.data) ?? 0;
                          const yMin = -50;
                          const maxPrice = Math.max(...priceElement.data.map((point: any) => 
                            typeof point === 'object' && point.y !== undefined ? point.y : point
                          ));
                          const yMax = Math.ceil(maxPrice / 50) * 50;
                          const yRange = yMax - yMin;
                          const normalizedValue = Math.max(0, Math.min(1, (yMax - lastValue) / yRange));
                          return `${fixedChartDimensions.current.top + (normalizedValue * fixedChartDimensions.current.height)}px`;
                        })(),
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(59, 130, 246, 0.95)',
                    color: '#ffffff',
                    padding: '0.225rem 0.375rem',
                    borderRadius: '0.1875rem',
                    fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    zIndex: 10000,
                    pointerEvents: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementDataLocal(priceElement.data) ?? 0;
                    return lastValue.toFixed(2);
                  })()}
                </div>
              )}

              {/* Revenue Last Value */}
              {revenueElement?.visible && revenueElement.data.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y1}px`,
                    top: lastValuePositions.revenue !== null
                      ? `${lastValuePositions.revenue}px`
                      : (() => {
                          // Fallback calculation while chart is loading
                          if (fixedChartDimensions.current.height === 0) return '0px';
                          const lastValue = getLastFiniteYFromElementDataLocal(revenueElement.data) ?? 0;
                          const yMin = -240000;
                          const yMax = 480000;
                          const yRange = yMax - yMin;
                          const normalizedValue = Math.max(0, Math.min(1, (yMax - lastValue) / yRange));
                          return `${fixedChartDimensions.current.top + (normalizedValue * fixedChartDimensions.current.height)}px`;
                        })(),
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(34, 197, 94, 0.95)',
                    color: '#ffffff',
                    padding: '0.225rem 0.375rem',
                    borderRadius: '0.1875rem',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    zIndex: 10000,
                    pointerEvents: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementDataLocal(revenueElement.data) ?? 0;
                    return formatRevenueWithUnits(lastValue);
                  })()}
                </div>
              )}

              {/* Gross Profit Last Value */}
              {(() => {
                const grossProfitElement = elements.find(e => e.id === 'gross-profit');
                return grossProfitElement?.visible && grossProfitElement.data.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y1}px`,
                      top: lastValuePositions.grossProfit !== null
                        ? `${lastValuePositions.grossProfit}px`
                        : (() => {
                            // Fallback calculation while chart is loading
                            if (fixedChartDimensions.current.height === 0) return '0px';
                            const lastValue = getLastFiniteYFromElementDataLocal(grossProfitElement.data) ?? 0;
                            const yMin = -240000;
                            const yMax = 480000;
                            const yRange = yMax - yMin;
                            const normalizedValue = Math.max(0, Math.min(1, (yMax - lastValue) / yRange));
                            return `${fixedChartDimensions.current.top + (normalizedValue * fixedChartDimensions.current.height)}px`;
                          })(),
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(139, 92, 246, 0.95)',
                      color: '#ffffff',
                      padding: '0.225rem 0.375rem',
                      borderRadius: '0.1875rem',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      zIndex: 10000,
                      pointerEvents: 'none',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementDataLocal(grossProfitElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* Net Income Last Value */}
              {(() => {
                const netIncomeElement = elements.find(e => e.id === 'net-income');
                return netIncomeElement?.visible && netIncomeElement.data.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${axisPositions.y1}px`,
                      top: lastValuePositions.netIncome !== null
                        ? `${lastValuePositions.netIncome}px`
                        : (() => {
                            // Fallback calculation while chart is loading
                            if (fixedChartDimensions.current.height === 0) return '0px';
                            const lastValue = getLastFiniteYFromElementDataLocal(netIncomeElement.data) ?? 0;
                            const yMin = -240000;
                            const yMax = 480000;
                            const yRange = yMax - yMin;
                            const normalizedValue = Math.max(0, Math.min(1, (yMax - lastValue) / yRange));
                            return `${fixedChartDimensions.current.top + (normalizedValue * fixedChartDimensions.current.height)}px`;
                          })(),
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(245, 158, 11, 0.95)',
                      color: 'white',
                      padding: '0.225rem 0.375rem',
                      borderRadius: '0.1875rem',
                      fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      zIndex: 10000,
                      pointerEvents: 'none',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }}
                  >
                    {(() => {
                      const lastValue = getLastFiniteYFromElementDataLocal(netIncomeElement.data) ?? 0;
                      return formatRevenueWithUnits(lastValue);
                    })()}
                  </div>
                );
              })()}

              {/* PE Last Value */}
              {peElement?.visible && peElement.data.length > 0 && axisPositions.y2 > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${axisPositions.y2}px`,
                    top: lastValuePositions.pe !== null
                      ? `${lastValuePositions.pe}px`
                      : (() => {
                          // Fallback calculation while chart is loading
                          if (fixedChartDimensions.current.height === 0) return '0px';
                          const lastValue = getLastFiniteYFromElementDataLocal(peElement.data) ?? 0;
                          const yMin = 0;
                          const yMax = 50;
                          const yRange = yMax - yMin;
                          const normalizedValue = Math.max(0, Math.min(1, (yMax - lastValue) / yRange));
                          return `${fixedChartDimensions.current.top + (normalizedValue * fixedChartDimensions.current.height)}px`;
                        })(),
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    color: '#ffffff',
                    padding: '0.25rem 0.375rem',
                    borderRadius: '0.1875rem',
                    fontSize: RIGHT_AXIS_TOOLTIP_FONT_SIZE,
                    width: `${RIGHT_AXIS_PE_TOOLTIP_WIDTH_PX}px`,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    fontWeight: 'normal',
                    whiteSpace: 'nowrap',
                    zIndex: 10000,
                    pointerEvents: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                >
                  {(() => {
                    const lastValue = getLastFiniteYFromElementDataLocal(peElement.data) ?? 0;
                    return (Math.floor(lastValue * 100) / 100).toFixed(2);
                  })()}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default AdvancedChart;

